import { useState, useEffect, useRef } from "react";
import axios from "axios";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { 
  Package, Search, Filter, Tag, Check, X, Edit, 
  ChevronDown, ChevronRight, Bell, Clock, CheckCircle,
  XCircle, AlertCircle, Layers, Box, Download,
  Upload, Plus, Trash2, Database, Users,
  MoreVertical, Undo2, Beaker,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import PermButton from "@/components/PermButton";
import RegrindRMFields from "@/components/RegrindRMFields";
import ConvertCategoryDialog from "@/components/ConvertCategoryDialog";
import { MouldSearchSelect } from "@/components/MouldSearchSelect";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { buildRmEditPayload, validateDualUomSelection } from "@/lib/rmEditPayload";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import Pagination from "../components/Pagination";
import useAuthStore from "@/store/authStore";
import { uploadWithReceipt, downloadBase64Xlsx, suggestReceiptFilename } from "@/lib/bulkUploadReceipt";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to convert database category to frontend format
const convertDbCategory = (dbCat) => {
  const fields = (dbCat.description_columns || []).map(col => col.key);
  const nameFormat = (dbCat.description_columns || [])
    .filter(col => col.include_in_name)
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map(col => col.key);
  
  return {
    name: dbCat.name,
    fields: fields,
    nameFormat: nameFormat,
    description_columns: dbCat.description_columns || [],
    default_uom: dbCat.default_uom,
    rm_id_prefix: dbCat.rm_id_prefix
  };
};

// Helper function to get category display name
const getCategoryName = (code, rmCategories) => {
  return rmCategories[code]?.name || code;
};

const RMRepository = () => {
  const [activeTab, setActiveTab] = useState("repository");
  const { hasRole, isMasterAdmin } = useAuthStore();
  const isAdmin = isMasterAdmin();
  const canManageRMs = isAdmin || hasRole('TECH_OPS_ENGINEER');
  
  // RM Categories from database (Tech Ops is single source of truth)
  const [rmCategories, setRmCategories] = useState({});
  
  // File input refs for upload
  const fileInputRef = useRef(null);
  const uomPatchInputRef = useRef(null);
  const migrateFileInputRef = useRef(null);
  
  // Data
  const [materials, setMaterials] = useState([]);
  const [brands, setBrands] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [models, setModels] = useState([]);
  const [rmRequests, setRmRequests] = useState([]);
  const [buyerSkuRequests, setBuyerSkuRequests] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingSkuCount, setPendingSkuCount] = useState(0);

  // Vendors-for-RM lookup dialog (Feb 2026) — opens from each RM row.
  // Shows every vendor tagged to the RM with their unit price, sourced
  // from `vendor_rm_prices`. Read-only view; edits still happen in
  // Vendor Management.
  const [showVendorsDialog, setShowVendorsDialog] = useState(false);
  const [vendorsForRM, setVendorsForRM] = useState([]);
  const [vendorsForRMLoading, setVendorsForRMLoading] = useState(false);
  const [vendorsForRMTarget, setVendorsForRMTarget] = useState(null);
  
  // Add RM dialog state
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showCreatedDialog, setShowCreatedDialog] = useState(false);
  const [showMigrateDialog, setShowMigrateDialog] = useState(false);
  const [createdRMs, setCreatedRMs] = useState([]);
  // Jul 2026 — per-row INM BOM-derive outcome from the last bulk
  // upload. Shape: [{row, rm_id, bom_written, reason}]. Rendered as a
  // BOM column in the Created-RMs dialog. Stable reason vocabulary
  // (see backend `_row_bom_outcome` construction).
  const [inmBomOutcomes, setInmBomOutcomes] = useState([]);
  const [migrating, setMigrating] = useState(false);
  const [selectedAddCategory, setSelectedAddCategory] = useState("");
  const [categoryData, setCategoryData] = useState({});
  const [lowStockThreshold, setLowStockThreshold] = useState(10);
  const [addUom, setAddUom] = useState("");
  const [addSourceType, setAddSourceType] = useState("");
  const [addDualUom, setAddDualUom] = useState(false);
  const [addProcurementUom, setAddProcurementUom] = useState("");
  const [addConsumptionUom, setAddConsumptionUom] = useState("");
  const [addConversionFactor, setAddConversionFactor] = useState("");

  // Phase B.6 — Mould-driven INP create state
  const [mouldSearchTerm, setMouldSearchTerm] = useState("");
  const [mouldOptions, setMouldOptions] = useState([]);
  const [mouldSearchLoading, setMouldSearchLoading] = useState(false);
  const [selectedMould, setSelectedMould] = useState(null);
  const [inpColourantOptions, setInpColourantOptions] = useState([]);
  const [selectedColourantId, setSelectedColourantId] = useState("");
  // Feb 2026 — new pantone-driven INP flow. The colourant SKU dropdown was
  // blocking RM creation because operators don't pre-create colourants.
  // Operators enter pantone + (optional) MB, backend find-or-mints.
  const [inpPantoneCode, setInpPantoneCode] = useState("");
  const [inpMbRmId, setInpMbRmId] = useState("");
  const [inpPreview, setInpPreview] = useState(null);
  const [inpPreviewLoading, setInpPreviewLoading] = useState(false);

  // Jul 2026 — INM is now pantone-driven (parity with INP mould-driven
  // create). The mandatory Colourant-SKU picker was removed; operators
  // enter pantone (already required, see categoryData.pantone_code) and
  // an OPTIONAL PWD `rm_id`. Backend find-or-mints the POWDER_COAT
  // colourant on (null, pantone, POWDER_COAT) and, if pwd_rm_id is
  // supplied, promotes it to PREFERRED for that colourant.
  const [inmPwdRmId, setInmPwdRmId] = useState("");

  // Feb 2026 — INMFAB-driven powder dosage (Commit 2)
  // ─────────────────────────────────────────────────
  // INMFAB creation captures powder_qty_grams + coating_scrap_factor
  // into category_data. INM creation binds to an INMFAB via top-level
  // parent_rm_id and carries a per-row source_type. `enrich_inm_l2_bom`
  // reads INMFAB-first with INM-master fallback (AC9). Dosage edits on
  // an existing INMFAB are refused with 409 — user must call the
  // explicit cascade endpoint.
  const [inmfabPowderQtyGrams, setInmfabPowderQtyGrams] = useState("");
  const [inmfabCoatingScrapFactor, setInmfabCoatingScrapFactor] = useState("0.10");
  const [inmfabDescription, setInmfabDescription] = useState("");

  // Jul 2026 — INMFAB dosage cascade dialog (view + edit through the
  // guarded preview→apply→undo flow). Direct PUT is refused with 409 by
  // design — see rm_convert_routes.py :693-747.
  const [linkedInmCounts, setLinkedInmCounts] = useState({}); // { INMFAB_rm_id: count }
  const [dosageDialogRm, setDosageDialogRm] = useState(null); // opens cascade dialog for this INMFAB
  const [dosageStep, setDosageStep] = useState("input"); // "input" | "preview" | "success"
  const [dosageNewPg, setDosageNewPg] = useState("");
  const [dosageNewScrap, setDosageNewScrap] = useState("");
  const [dosageReason, setDosageReason] = useState("");
  const [dosagePreview, setDosagePreview] = useState(null);
  const [dosageResult, setDosageResult] = useState(null);
  const [dosageBusy, setDosageBusy] = useState(false);
  const [inmParentRmId, setInmParentRmId] = useState("");
  const [inmParentSearchTerm, setInmParentSearchTerm] = useState("");
  const [inmParentOptions, setInmParentOptions] = useState([]);
  const [inmParentLoading, setInmParentLoading] = useState(false);
  const [inmSourceType, setInmSourceType] = useState("MANUFACTURED");

  // Convert Category (Jul 2026 P1) — MasterAdmin-only reversible flow.
  const [convertTargetRm, setConvertTargetRm] = useState(null);
  
  // Loading
  const [loading, setLoading] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    search: "",
    category: "",
    brand_id: "",
    vertical_id: "",
    model_id: "",
    is_brand_specific: "",
    source_type: "",
    bom_level: "",
    colourant_id: "",
    pantone_code: "",
  });
  // Master lists for the new Colourant / Pantone filter pickers (lazy-loaded once)
  const [pantoneShadesMaster, setPantoneShadesMaster] = useState([]);
  const [colourantSkusMaster, setColourantSkusMaster] = useState([]);
  
  // Selection for bulk operations
  const [selectedRMs, setSelectedRMs] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Dialogs
  const [showTagDialog, setShowTagDialog] = useState(false);
  const [showBulkTagDialog, setShowBulkTagDialog] = useState(false);
  const [showRequestDetailDialog, setShowRequestDetailDialog] = useState(false);
  
  // Editing
  const [editingRM, setEditingRM] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  // Tag form
  const [tagForm, setTagForm] = useState({
    brand_ids: [],
    vertical_ids: [],
    model_ids: [],
    is_brand_specific: false,
    uom: "",
    source_type: "",
    dual_uom: false,
    procurement_uom: "",
    consumption_uom: "",
    conversion_factor: ""
  });
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Price History tab state
  const [priceStats, setPriceStats] = useState(null);
  const [avgPrices, setAvgPrices] = useState([]);
  const [priceHistory, setPriceHistory] = useState([]);
  const [priceFilter, setPriceFilter] = useState({ rm_id: "", vendor_id: "" });

  // Price Master tab — global view of resolved prices across all RMs
  const [priceMasterRows, setPriceMasterRows] = useState([]);
  const [priceMasterCounts, setPriceMasterCounts] = useState(null);
  const [priceMasterLoading, setPriceMasterLoading] = useState(false);
  const [priceMasterFilter, setPriceMasterFilter] = useState({
    category: "",
    source: "",
    search: "",
  });
  const [priceMasterPage, setPriceMasterPage] = useState(1);
  const [priceMasterPagination, setPriceMasterPagination] = useState({ total: 0, total_pages: 1, page: 1, page_size: 20 });
  const PRICE_MASTER_PAGE_SIZE = 20;
  const [priceUploading, setPriceUploading] = useState(false);
  const [priceUploadMode, setPriceUploadMode] = useState("append");
  const priceFileInputRef = useRef(null);

  useEffect(() => {
    fetchMasterData();
    fetchRmCategories();
    fetchPendingCount();
    fetchPendingSkuCount();
  }, []);

  useEffect(() => {
    if (activeTab === "repository") {
      fetchMaterials();
    } else if (activeTab === "requests") {
      fetchRMRequests();
    } else if (activeTab === "sku-requests") {
      fetchBuyerSkuRequests();
    } else if (activeTab === "price-history") {
      fetchPriceData();
    } else if (activeTab === "price-master") {
      fetchPriceMaster();
    }
  }, [activeTab, filters, currentPage, pageSize]);

  // Re-fetch Price Master when its own pagination changes
  useEffect(() => {
    if (activeTab === "price-master") {
      fetchPriceMaster();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceMasterPage]);

  // Lazy-load the Pantone shade + Colourant SKU master lists once (used by
  // the new "Filter by Colourant / Pantone" inputs). Tolerant of failure.
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/pantone/shades`, {
          params: { status: "ACTIVE", limit: 1000 },
        });
        setPantoneShadesMaster(r.data?.shades || r.data?.items || []);
      } catch (_e) { /* non-fatal */ }
      try {
        // Paginate Colourant SKUs (500 cap per page)
        const all = [];
        let skip = 0;
        const lim = 500;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const r2 = await axios.get(`${API}/colourway/colourant-skus`, {
            params: { limit: lim, skip },
          });
          const items = r2.data?.items || [];
          all.push(...items);
          const total = r2.data?.total ?? items.length;
          skip += items.length;
          if (items.length < lim || skip >= total) break;
        }
        setColourantSkusMaster(all);
      } catch (_e) { /* non-fatal */ }
    })();
  }, []);

  // Fetch RM Categories from database
  const fetchRmCategories = async () => {
    try {
      const response = await axios.get(`${API}/rm-categories`);
      const dbCategories = response.data || {};
      setRmCategories(dbCategories);
    } catch (error) {
      console.error("Failed to fetch RM categories:", error);
      setRmCategories({});
    }
  };

  const fetchMasterData = async () => {
    try {
      const [brandsRes, verticalsRes, modelsRes] = await Promise.all([
        axios.get(`${API}/brands`),
        axios.get(`${API}/verticals`),
        axios.get(`${API}/models`)
      ]);
      setBrands(brandsRes.data.filter(b => b.status === 'ACTIVE'));
      setVerticals(verticalsRes.data.filter(v => v.status === 'ACTIVE'));
      setModels(modelsRes.data.filter(m => m.status === 'ACTIVE'));
    } catch (error) {
      console.error("Failed to fetch master data");
    }
  };

  // Jul 2026 — When materials load with INMFAB rows visible, pull the
  // linked INM count once so the description cell + cascade dialog can
  // surface it. Uses the paginated list endpoint. Failure is tolerated.
  useEffect(() => {
    const inmfabs = (materials || []).filter(r => (r.category || "").toUpperCase() === "INMFAB");
    if (inmfabs.length === 0) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/raw-materials`, {
          params: { category: "INM", page: 1, page_size: 5000 },
        });
        const inms = res.data?.items || [];
        const counts = {};
        inms.forEach(inm => {
          const p = (inm.parent_rm_id || "").toUpperCase();
          if (p) counts[p] = (counts[p] || 0) + 1;
        });
        setLinkedInmCounts(counts);
      } catch (e) {
        console.warn("Linked INM count fetch failed:", e.message);
      }
    })();
  }, [materials]);

  // ── INMFAB dosage cascade helpers (Jul 2026) ──────────────────
  const openDosageDialog = (rm) => {
    const cur = rm?.category_data || {};
    setDosageDialogRm(rm);
    setDosageStep("input");
    setDosageNewPg(String(cur.powder_qty_grams ?? ""));
    setDosageNewScrap(String(cur.coating_scrap_factor ?? "0.10"));
    setDosageReason("");
    setDosagePreview(null);
    setDosageResult(null);
  };
  const closeDosageDialog = () => {
    setDosageDialogRm(null);
    setDosageStep("input");
    setDosagePreview(null);
    setDosageResult(null);
  };
  const runDosagePreview = async () => {
    if (!dosageDialogRm) return;
    const pg = parseFloat(dosageNewPg);
    const sc = parseFloat(dosageNewScrap);
    if (!(pg > 0)) { toast.error("Powder Qty (grams) must be > 0"); return; }
    if (isNaN(sc) || sc < 0) { toast.error("Scrap factor must be ≥ 0"); return; }
    setDosageBusy(true);
    try {
      const res = await axios.get(
        `${API}/raw-materials/inmfab/${dosageDialogRm.rm_id}/cascade-preview`,
        { params: { powder_qty_grams: pg, coating_scrap_factor: sc } },
      );
      setDosagePreview(res.data);
      setDosageStep("preview");
    } catch (e) {
      const d = e.response?.data?.detail || e.originalDetail || e.message;
      toast.error(`Preview failed: ${typeof d === "object" ? JSON.stringify(d) : d}`);
    } finally {
      setDosageBusy(false);
    }
  };
  const applyDosageCascade = async () => {
    if (!dosageDialogRm) return;
    const pg = parseFloat(dosageNewPg);
    const sc = parseFloat(dosageNewScrap);
    setDosageBusy(true);
    try {
      const res = await axios.post(
        `${API}/raw-materials/inmfab/${dosageDialogRm.rm_id}/cascade-apply`,
        { powder_qty_grams: pg, coating_scrap_factor: sc, reason: dosageReason || "inmfab_dosage_update" },
      );
      setDosageResult(res.data);
      setDosageStep("success");
      toast.success(`Cascade applied — ${res.data.rewritten || 0} INM(s) rewritten`);
      fetchMaterials();
    } catch (e) {
      const d = e.response?.data?.detail || e.originalDetail || e.message;
      const msg = typeof d === "object" ? (d.message || JSON.stringify(d)) : d;
      toast.error(`Cascade blocked: ${msg}`, { duration: 10000 });
    } finally {
      setDosageBusy(false);
    }
  };
  const undoDosageCascade = async () => {
    if (!dosageResult?.cascade_id) return;
    if (!window.confirm(`Undo cascade ${dosageResult.cascade_id.slice(0, 8)}…? This restores the prior INMFAB dosage AND every linked INM's rm_bom.`)) return;
    setDosageBusy(true);
    try {
      const res = await axios.post(
        `${API}/raw-materials/inmfab/cascade/undo/${dosageResult.cascade_id}`,
      );
      toast.success(`Cascade undone — ${res.data.rewritten || res.data.restored || 0} INM(s) restored`);
      closeDosageDialog();
      fetchMaterials();
    } catch (e) {
      const d = e.response?.data?.detail || e.originalDetail || e.message;
      toast.error(`Undo failed: ${typeof d === "object" ? JSON.stringify(d) : d}`);
    } finally {
      setDosageBusy(false);
    }
  };

  const fetchMaterials = async () => {
    setLoading(true);
    try {
      // Build query params
      let url = `${API}/raw-materials/by-tags?page=${currentPage}&page_size=${pageSize}`;
      if (filters.brand_id) url += `&brand_id=${filters.brand_id}`;
      if (filters.vertical_id) url += `&vertical_id=${filters.vertical_id}`;
      if (filters.model_id) url += `&model_id=${filters.model_id}`;
      if (filters.is_brand_specific === "true") url += `&is_brand_specific=true`;
      if (filters.is_brand_specific === "false") url += `&is_brand_specific=false`;
      if (filters.category) url += `&category=${filters.category}`;
      if (filters.source_type) url += `&source_type=${filters.source_type}`;
      if (filters.bom_level) url += `&bom_level=${filters.bom_level}`;
      if (filters.colourant_id) url += `&colourant_id=${encodeURIComponent(filters.colourant_id)}`;
      if (filters.pantone_code) url += `&pantone_code=${encodeURIComponent(filters.pantone_code)}`;
      if (filters.search) url += `&search=${encodeURIComponent(filters.search)}`;
      
      const res = await axios.get(url);
      setMaterials(res.data.items || []);
      setTotalItems(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
      setSelectedRMs(new Set());
      setSelectAll(false);
    } catch (error) {
      toast.error("Failed to fetch materials");
    } finally {
      setLoading(false);
    }
  };

  // Export all RMs to Excel with each category as a separate sheet
  const handleExportRepository = async () => {
    try {
      toast.info("Preparing RM Repository export...");
      
      // Fetch ALL raw materials (no pagination)
      const res = await axios.get(`${API}/raw-materials?page_size=50000`);
      const allRMs = res.data || [];
      
      if (allRMs.length === 0) {
        toast.error("No raw materials to export");
        return;
      }
      
      // Group RMs by category
      const categorizedRMs = {};
      allRMs.forEach(rm => {
        const cat = rm.category || rm.rm_id?.split('_')[0] || 'OTHER';
        if (!categorizedRMs[cat]) {
          categorizedRMs[cat] = [];
        }
        categorizedRMs[cat].push(rm);
      });
      
      // Create workbook
      const wb = XLSX.utils.book_new();
      
      // Add summary sheet first
      const summaryData = Object.entries(categorizedRMs).sort((a, b) => a[0].localeCompare(b[0])).map(([cat, rms]) => ({
        'Category Code': cat,
        'Category Name': getCategoryName(cat, rmCategories),
        'Total RMs': rms.length
      }));
      summaryData.push({ 'Category Code': '', 'Category Name': 'TOTAL', 'Total RMs': allRMs.length });
      const summaryWs = XLSX.utils.json_to_sheet(summaryData);
      summaryWs['!cols'] = [{ wch: 15 }, { wch: 25 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');
      
      // Common columns that appear on every category sheet
      const commonColumns = [
        { key: 'rm_id', header: 'RM ID' },
        { key: 'description', header: 'Description' },
        { key: 'source_type', header: 'Source Type' },
        { key: 'uom', header: 'UOM' },
        { key: 'dual_uom', header: 'Dual UOM' },
        { key: 'procurement_uom', header: 'Procurement UOM' },
        { key: 'consumption_uom', header: 'Consumption UOM' },
        { key: 'conversion_factor', header: 'Conversion Factor' },
        { key: 'low_stock_threshold', header: 'Low Stock Threshold' },
        { key: 'hsn_code', header: 'HSN Code' },
        { key: 'gst_rate', header: 'GST Rate (%)' },
        { key: 'min_order_qty', header: 'Min Order Qty' },
        { key: 'lead_time_days', header: 'Lead Time (Days)' },
        { key: 'status', header: 'Status' },
        { key: 'created_at', header: 'Created At' }
      ];

      // Pre-load Colourant SKUs (for resolving INP/INMFAB.colourant_ids) and
      // Pantone master (for the human Pantone code). One-shot fetch, joined
      // client-side. Tolerant of failure — if the fetch fails we just skip
      // the derived columns and the export still works.
      let colourantMap = null;  // {colourant_id: {pantone_code, part_id, polymer_base}}
      try {
        colourantMap = {};
        const pageLimit = 500;  // server-side cap
        let skip = 0;
        // Paginate until we've fetched every colourant SKU. Bounded by
        // total returned in the first page to avoid runaway loops.
        // eslint-disable-next-line no-constant-condition
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const csRes = await axios.get(`${API}/colourway/colourant-skus`, {
            params: { limit: pageLimit, skip },
          });
          const list = csRes.data?.items || [];
          list.forEach((c) => {
            if (c && c.colourant_id) colourantMap[c.colourant_id] = c;
          });
          const total = csRes.data?.total ?? list.length;
          skip += list.length;
          if (list.length < pageLimit || skip >= total) break;
        }
        if (Object.keys(colourantMap).length === 0) colourantMap = null;
      } catch (_e) {
        colourantMap = null;
      }

      const resolveColourants = (rm) => {
        const ids = (rm?.category_data?.colourant_ids) || [];
        if (!Array.isArray(ids) || ids.length === 0 || !colourantMap) {
          return { colourant_ids: '', pantones: '' };
        }
        const resolved = ids.map((id) => colourantMap[id]).filter(Boolean);
        return {
          colourant_ids: resolved.map((r) => r.colourant_id).join(', '),
          pantones: resolved.map((r) => r.pantone_code).join(', '),
        };
      };
      
      // Create a sheet for each category with its specific schema columns
      Object.entries(categorizedRMs).sort((a, b) => a[0].localeCompare(b[0])).forEach(([category, rms]) => {
        const catConfig = rmCategories[category] || {};
        const descCols = catConfig.description_columns || [];
        
        // Category-specific columns (from rm_categories.description_columns)
        const catSpecificColumns = descCols
          .slice()
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map(col => ({
            key: `category_data.${col.key}`,
            header: col.label || col.key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
          }));

        // INP / INMFAB get two derived Colourant columns appended at the end —
        // resolved client-side from category_data.colourant_ids.
        const isColourablePart = (category === 'INP' || category === 'INMFAB' || category === 'INM');
        const colourantColumns = (isColourablePart && colourantMap) ? [
          { key: '__colourant_skus__', header: 'Colourant SKU(s)' },
          { key: '__pantone_derived__', header: 'Pantone (from Colourant)' },
        ] : [];

        // Merge: common + cat-specific + derived (if applicable)
        const columns = [...commonColumns, ...catSpecificColumns, ...colourantColumns];
        
        const sheetData = rms.map(rm => {
          const row = {};
          const resolved = isColourablePart ? resolveColourants(rm) : null;
          columns.forEach(col => {
            let value;
            if (col.key === '__colourant_skus__') {
              value = resolved?.colourant_ids || '';
            } else if (col.key === '__pantone_derived__') {
              value = resolved?.pantones || '';
            } else if (col.key.startsWith('category_data.')) {
              const innerKey = col.key.split('.')[1];
              value = rm.category_data?.[innerKey];
            } else {
              value = rm[col.key];
            }
            if (col.key === 'created_at' && value) {
              try { value = new Date(value).toLocaleDateString(); } catch(e) { /* keep raw */ }
            }
            if (typeof value === 'boolean') value = value ? 'Yes' : 'No';
            row[col.header] = value ?? '';
          });
          return row;
        });
        
        const ws = XLSX.utils.json_to_sheet(sheetData, {
          header: columns.map(c => c.header)
        });
        ws['!cols'] = columns.map(c => ({ wch: Math.max(14, c.header.length + 2) }));
        
        // Sheet name: max 31 chars, no special chars
        const catName = getCategoryName(category, rmCategories);
        const sheetName = `${category} - ${catName}`.replace(/[\\/?*[\]]/g, '').slice(0, 31);
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });
      
      // Generate and download file using native approach
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const filename = `RM_Repository_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      // Use native download link (more reliable than file-saver in some environments)
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${allRMs.length} RMs across ${Object.keys(categorizedRMs).length} categories`);
    } catch (error) {
      console.error("Export failed:", error?.message, error?.stack);
      toast.error(`Failed to export RM repository: ${error?.message || 'Unknown error'}`);
    }
  };

  const fetchRMRequests = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/rm-requests`);
      setRmRequests(res.data);
    } catch (error) {
      toast.error("Failed to fetch RM requests");
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingCount = async () => {
    try {
      const res = await axios.get(`${API}/rm-requests/pending-count`);
      setPendingCount(res.data.pending_count);
    } catch (error) {
      console.error("Failed to fetch pending count");
    }
  };

  const fetchPendingSkuCount = async () => {
    try {
      const res = await axios.get(`${API}/demand-hub/buyer-sku-requests/pending-count`);
      setPendingSkuCount(res.data.pending_count);
    } catch (error) {
      console.error("Failed to fetch pending SKU count");
    }
  };

  const fetchBuyerSkuRequests = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/demand-hub/buyer-sku-requests`);
      setBuyerSkuRequests(res.data);
    } catch (error) {
      toast.error("Failed to fetch Buyer SKU requests");
    } finally {
      setLoading(false);
    }
  };

  const handleReviewBuyerSkuRequest = async (requestId, action, notes = "") => {
    try {
      await axios.post(`${API}/demand-hub/buyer-sku-requests/${requestId}/review`, {
        action,
        review_notes: notes
      });
      toast.success(`Request ${action.toLowerCase()}ed`);
      fetchBuyerSkuRequests();
      fetchPendingSkuCount();
    } catch (error) {
      toast.error(`Failed to ${action.toLowerCase()} request`);
    }
  };

  // ============ Price History handlers ============
  const fetchPriceData = async () => {
    try {
      const [statsRes, avgRes] = await Promise.all([
        axios.get(`${API}/rm-prices/stats`),
        axios.get(`${API}/rm-prices/avg-prices`)
      ]);
      setPriceStats(statsRes.data);
      setAvgPrices(avgRes.data.items || []);
      await fetchPriceHistory();
    } catch (error) {
      toast.error("Failed to fetch price data");
    }
  };

  const fetchPriceHistory = async (overrides = {}) => {
    try {
      const params = new URLSearchParams({ page_size: 200 });
      const rmFilter = overrides.rm_id !== undefined ? overrides.rm_id : priceFilter.rm_id;
      const vFilter = overrides.vendor_id !== undefined ? overrides.vendor_id : priceFilter.vendor_id;
      if (rmFilter) params.append("rm_id", rmFilter);
      if (vFilter) params.append("vendor_id", vFilter);
      const res = await axios.get(`${API}/rm-prices/history?${params}`);
      setPriceHistory(res.data.items || []);
    } catch (error) {
      console.error("Failed to fetch price history", error);
    }
  };

  // ============ Price Master tab ============
  const fetchPriceMaster = async (overrides = {}) => {
    setPriceMasterLoading(true);
    try {
      const params = new URLSearchParams();
      const f = { ...priceMasterFilter, ...overrides };
      if (f.category) params.append("category", f.category);
      if (f.source) params.append("source", f.source);
      if (f.search) params.append("search", f.search);
      // Pagination — default 20 RMs per page
      const pageToFetch = overrides.__resetPage ? 1 : priceMasterPage;
      params.append("page", pageToFetch);
      params.append("page_size", PRICE_MASTER_PAGE_SIZE);
      const res = await axios.get(`${API}/rm-prices/price-master?${params}`);
      setPriceMasterRows(res.data.items || []);
      setPriceMasterCounts(res.data.counts || null);
      setPriceMasterPagination({
        total: res.data.total || 0,
        total_pages: res.data.total_pages || 1,
        page: res.data.page || 1,
        page_size: res.data.page_size || PRICE_MASTER_PAGE_SIZE,
      });
      // Backend may clamp page if filter shrank the set — sync it back
      if (res.data.page && res.data.page !== priceMasterPage) {
        setPriceMasterPage(res.data.page);
      }
    } catch (e) {
      toast.error("Failed to load Price Master");
    } finally {
      setPriceMasterLoading(false);
    }
  };

  // Helper: apply a filter change and reset to page 1
  const applyPriceMasterFilter = (next) => {
    setPriceMasterFilter(next);
    setPriceMasterPage(1);
    // Fetch with the new filter immediately (state update is async)
    fetchPriceMaster({ ...next, __resetPage: true });
  };

  const exportPriceMaster = async () => {
    try {
      const params = new URLSearchParams();
      if (priceMasterFilter.category) params.append("category", priceMasterFilter.category);
      if (priceMasterFilter.source) params.append("source", priceMasterFilter.source);
      if (priceMasterFilter.search) params.append("search", priceMasterFilter.search);
      const res = await axios.get(`${API}/rm-prices/price-master/export?${params}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rm_price_master_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to export Price Master");
    }
  };

  const handlePriceUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPriceUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const data = await uploadWithReceipt(
        `${API}/rm-prices/upload?mode=${priceUploadMode}`,
        formData,
        {
          axiosConfig: { headers: { "Content-Type": "multipart/form-data" } },
          showToast: false,
        }
      );
      toast.success((data.message || "Upload complete") + " — Receipt downloaded.");
      const summary = data.summary || {};
      if (summary.errors > 0) {
        toast.warning(`${summary.errors} row(s) errored — see receipt for details.`, { duration: 8000 });
      }
      await fetchPriceData();
    } catch (err) {
      toast.error(`Upload failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setPriceUploading(false);
      e.target.value = "";
    }
  };

  const downloadPriceTemplate = () => {
    window.open(`${API}/rm-prices/template`, "_blank");
  };

  const handleSelectRM = (rmId) => {
    const newSelected = new Set(selectedRMs);
    if (newSelected.has(rmId)) {
      newSelected.delete(rmId);
    } else {
      newSelected.add(rmId);
    }
    setSelectedRMs(newSelected);
    setSelectAll(newSelected.size === materials.length);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRMs(new Set());
    } else {
      setSelectedRMs(new Set(materials.map(m => m.rm_id)));
    }
    setSelectAll(!selectAll);
  };

  const handleEditTags = (rm) => {
    setEditingRM(rm);
    // Get category default UOM and source_type for display
    const catConfig = rmCategories[rm.category] || {};
    setTagForm({
      brand_ids: rm.brand_ids || [],
      vertical_ids: rm.vertical_ids || [],
      model_ids: rm.model_ids || [],
      is_brand_specific: rm.is_brand_specific || false,
      category: rm.category || "",
      uom: rm.uom || catConfig.default_uom || "PCS",
      source_type: rm.source_type || catConfig.default_source_type || "PURCHASED",
      dual_uom: rm.dual_uom || false,
      procurement_uom: rm.procurement_uom || "",
      consumption_uom: rm.consumption_uom || "",
      conversion_factor: rm.conversion_factor || ""
    });
    setShowTagDialog(true);
  };

  const handleSaveTags = async () => {
    if (!editingRM) return;

    // Sep 2026 (PM_00241–243 incident) — dual-UOM requires an explicit
    // selection in BOTH UOM dropdowns. The Selects no longer fake a
    // default, so an untouched dropdown is empty state; block the save
    // rather than ship "" which backend Case C (`rm_routes.py:5664-5667`)
    // coerces to null.
    const uomError = validateDualUomSelection(tagForm);
    if (uomError) {
      toast.error(uomError);
      return;
    }

    try {
      // Feb 2026 fix — the payload is narrowed via `buildRmEditPayload`
      // so we no longer ship `dual_uom: false` on every save from a
      // non-dual RM's edit dialog. Prior behaviour tripped
      // `update_raw_material`'s Case A and silently nulled
      // procurement_uom / consumption_uom / conversion_factor on
      // unrelated edits (Diken 2026-09-17).
      const payload = buildRmEditPayload(tagForm, editingRM);
      await axios.put(`${API}/raw-materials/${editingRM.rm_id}`, payload);
      toast.success(`Tags updated for ${editingRM.rm_id}`);
      setShowTagDialog(false);
      setEditingRM(null);
      fetchMaterials();
    } catch (error) {
      toast.error("Failed to update tags");
    }
  };

  const handleBulkTag = async () => {
    if (selectedRMs.size === 0) {
      toast.error("No RMs selected");
      return;
    }
    
    try {
      // Update each selected RM
      const promises = Array.from(selectedRMs).map(rmId => 
        axios.post(`${API}/raw-materials/${rmId}/tag`, tagForm)
      );
      await Promise.all(promises);
      
      toast.success(`Tags added to ${selectedRMs.size} RMs`);
      setShowBulkTagDialog(false);
      setTagForm({ brand_ids: [], vertical_ids: [], model_ids: [], is_brand_specific: false });
      fetchMaterials();
    } catch (error) {
      toast.error("Failed to bulk tag RMs");
    }
  };

  const handleReviewRequest = async (requestId, action, notes = "") => {
    try {
      await axios.post(`${API}/rm-requests/${requestId}/review`, {
        action,
        review_notes: notes
      });
      toast.success(`Request ${action.toLowerCase()}ed`);
      fetchRMRequests();
      fetchPendingCount();
      setShowRequestDetailDialog(false);
    } catch (error) {
      toast.error(`Failed to ${action.toLowerCase()} request`);
    }
  };

  const getFilteredModels = () => {
    if (!filters.vertical_id) return models;
    return models.filter(m => m.vertical_id === filters.vertical_id);
  };

  const getBrandName = (id) => brands.find(b => b.id === id)?.name || id;
  const getBrandCode = (id) => brands.find(b => b.id === id)?.code || "";
  const getVerticalName = (id) => verticals.find(v => v.id === id)?.name || id;
  const getVerticalCode = (id) => verticals.find(v => v.id === id)?.code || "";
  const getModelName = (id) => models.find(m => m.id === id)?.name || id;
  const getModelCode = (id) => models.find(m => m.id === id)?.code || "";

  const clearFilters = () => {
    setFilters({
      search: "",
      category: "",
      brand_id: "",
      vertical_id: "",
      model_id: "",
      is_brand_specific: "",
      source_type: "",
      bom_level: "",
      colourant_id: "",
      pantone_code: "",
    });
    setCurrentPage(1);
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  // Handle filter changes with page reset
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  // ==================== BULK UPLOAD & ADD RM FUNCTIONS ====================
  
  const handleBulkUpload = async (e, opts = {}) => {
    const file = e.target.files[0];
    if (!file) return;

    const updateExisting = !!opts.updateExisting;

    const formData = new FormData();
    formData.append('file', file);

    try {
      toast.info(updateExisting ? "Patching UOMs on existing RMs..." : "Processing upload...");
      const url = `${API}/raw-materials/bulk-upload${updateExisting ? "?update_existing=true" : ""}`;
      const response = await axios.post(url, formData);
      const { created, updated, skipped, errors, total_errors, total_duplicates, mode, created_rms, total_matched_existing, total_reactivated, matched_existing_rms, pantone_shades_created, inm_bom_outcomes, total_inm_bom_outcomes, total_inm_bom_not_written } = response.data;

      // UOM patch mode — only `updated` matters
      if (updateExisting) {
        if (updated > 0 && total_errors === 0) {
          toast.success(`Patched UOMs on ${updated} RM${updated === 1 ? '' : 's'}. Downloading receipt...`, { duration: 5000 });
        } else if (updated > 0 && total_errors > 0) {
          toast.warning(`Patched ${updated} RMs. ${total_errors} row(s) had errors: ${(errors || []).slice(0, 2).join('; ')}. Downloading receipt of patched rows...`, { duration: 8000 });
        } else if (total_errors > 0) {
          toast.error(`0 RMs patched. ${total_errors} errors: ${(errors || []).slice(0, 2).join('; ')}`, { duration: 10000 });
        } else {
          toast.warning('No RMs patched. Ensure the file has RM Code + at least one UOM column filled.', { duration: 8000 });
        }

        // Auto-download a receipt of the patched RMs (post-update state)
        const patchedIds = (response.data.updated_rms || []).map(u => u.rm_id);
        if (patchedIds.length) {
          try {
            const receipt = await axios.post(
              `${API}/raw-materials/export-by-ids`,
              { ids: patchedIds, sheet_name: "UOM Update Receipt" },
              { responseType: "blob" }
            );
            const blob = new Blob([receipt.data], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            const stamp = new Date().toISOString().replace(/[:T]/g, "-").split(".")[0];
            a.href = url;
            a.download = `uom-update-receipt-${stamp}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
          } catch (rxErr) {
            toast.warning("Patch saved, but the receipt download failed. You can re-export from Export All RMs.", { duration: 6000 });
          }
        }

        fetchMaterials();
        e.target.value = null;
        return;
      }

      if (total_duplicates > 0) {
        if (created > 0) {
          toast.success(`Created ${created} RMs. ${total_duplicates} duplicates skipped.`, { duration: 4000 });
          setCreatedRMs(created_rms || []);
          setShowCreatedDialog(true);
        } else {
          const dupList = response.data.duplicates?.slice(0, 5).map(d => d.rm_id).join(", ") || '';
          toast.warning(
            `All ${total_duplicates} RMs already exist. Duplicates: ${dupList}${total_duplicates > 5 ? '...' : ''}. Use "Update UOMs" to patch existing rows.`,
            { duration: 8000 }
          );
        }
      } else if (created > 0) {
        const modeText = mode === 'import_with_ids' ? ' (with existing codes)' : ' (new codes generated)';
        const matchBit = total_matched_existing
          ? ` — ${total_matched_existing} row(s) mapped to existing RMs`
          : '';
        const reactBit = total_reactivated
          ? ` — ${total_reactivated} previously-inactive RM(s) re-activated`
          : '';
        const pantoneBit = (pantone_shades_created && pantone_shades_created.length)
          ? ` — ${pantone_shades_created.length} new Pantone shade${pantone_shades_created.length === 1 ? '' : 's'} auto-seeded`
          : '';
        toast.success(`Created ${created} raw materials${modeText}${matchBit}${reactBit}${pantoneBit}. Receipt downloaded.`, { duration: 6000 });
        // Auto-download the generic line-item receipt
        if (response.data.report_xlsx_b64) {
          downloadBase64Xlsx(response.data.report_xlsx_b64, suggestReceiptFilename(`${API}/raw-materials/bulk-upload`));
        }
        setCreatedRMs(created_rms || []);
        setShowCreatedDialog(true);
      } else if (total_matched_existing > 0) {
        // Dedup-only outcome: nothing new, every row mapped to an existing RM.
        const list = (matched_existing_rms || []).slice(0, 5).map(m => m.rm_id).join(", ");
        toast.warning(
          `All ${total_matched_existing} rows already exist in the RM catalogue (${list}${total_matched_existing > 5 ? "..." : ""}). Receipt downloaded.`,
          { duration: 8000 }
        );
        if (response.data.report_xlsx_b64) {
          downloadBase64Xlsx(response.data.report_xlsx_b64, suggestReceiptFilename(`${API}/raw-materials/bulk-upload`));
        }
      } else if (total_errors > 0) {
        const errorMsg = errors?.slice(0, 2).join('; ') || 'Check file format';
        toast.error(`0 RMs created. ${total_errors} errors: ${errorMsg}`, { duration: 10000 });
      } else {
        toast.warning('No data found in file. Ensure file has data rows with RM Code or Category column.');
      }

      // Jul 2026 — per-row INM BOM outcome. Stashes the list for the
      // Created-RMs dialog to render, and warns the user of any INM
      // rows that landed without a BOM. The RM was still created —
      // only the BOM derive was skipped/failed.
      setInmBomOutcomes(inm_bom_outcomes || []);
      if (total_inm_bom_not_written && total_inm_bom_not_written > 0) {
        toast.warning(
          `${total_inm_bom_not_written} of ${total_inm_bom_outcomes} INM row(s) created with no BOM derived — see the Created-RMs dialog for per-row reasons.`,
          { duration: 12000 }
        );
      }
      
      fetchMaterials();
    } catch (error) {
      const errData = error.response?.data;
      toast.error(errData?.detail || errData?.message || "Upload failed - check file format", { duration: 8000 });
    }
    
    e.target.value = null;
  };

  const handleAddRM = async () => {
    if (!selectedAddCategory) {
      toast.error("Please select a category");
      return;
    }
    // Feb 2026: Pantone is now compulsory for INM creation.
    if (selectedAddCategory === "INM") {
      const pc = (categoryData.pantone_code || "").trim();
      if (!pc) {
        toast.error("Pantone Code is required for INM");
        return;
      }
      if (inmSourceType === "MANUFACTURED" && !inmParentRmId) {
        toast.error("MANUFACTURED INM requires an INMFAB parent RM ID");
        return;
      }
    }
    // Feb 2026 (Commit 2): INMFAB requires dosage fields for the
    // downstream INM cascade to work.
    if (selectedAddCategory === "INMFAB") {
      const pg = parseFloat(inmfabPowderQtyGrams);
      if (!(pg > 0)) {
        toast.error("INMFAB: Powder Qty (grams) must be > 0");
        return;
      }
    }
    // Aug 2026 (BS Variant) — Variant Name is mandatory identity on
    // BS creations. Block client-side before the POST so the user
    // gets a clear inline message; the backend's 400 is the
    // authoritative gate (belt + suspenders).
    if (selectedAddCategory === "BS") {
      const vn = (categoryData.variant_name || "").trim();
      if (!vn) {
        toast.error("Variant Name is required for Brand Assets");
        return;
      }
    }
    // Sep 2026 (PM_00241–243 incident) — same dual-UOM guard as the
    // edit dialog: an untouched dropdown is empty state, never a
    // hidden default. Block before the POST so "" never ships (the
    // create path stores it as-is: `rm_routes.py:1702-1703`).
    const addUomError = validateDualUomSelection({
      dual_uom: addDualUom,
      procurement_uom: addProcurementUom,
      consumption_uom: addConsumptionUom,
    });
    if (addUomError) {
      toast.error(addUomError);
      return;
    }

    try {
      // Jul 2026: INM sends `pantone_code` (already in categoryData) plus
      // optional `pwd_rm_id` so the backend can find-or-mint the
      // POWDER_COAT colourant and promote the PWD to PREFERRED in one
      // atomic create. Feb 2026 (Commit 2): INM also sends top-level
      // `parent_rm_id` (INMFAB) + `source_type`; INMFAB stamps
      // `powder_qty_grams` + `coating_scrap_factor` into category_data.
      let cdata = categoryData;
      if (selectedAddCategory === "INM" && inmPwdRmId && inmPwdRmId.trim()) {
        cdata = { ...categoryData, pwd_rm_id: inmPwdRmId.trim() };
      }
      if (selectedAddCategory === "INMFAB") {
        cdata = {
          ...categoryData,
          powder_qty_grams: parseFloat(inmfabPowderQtyGrams),
          coating_scrap_factor: parseFloat(inmfabCoatingScrapFactor || "0.10"),
        };
        if (inmfabDescription && inmfabDescription.trim()) {
          cdata.description = inmfabDescription.trim();
          cdata.name = inmfabDescription.trim();
        }
      }
      const isInm = selectedAddCategory === "INM";
      const _addResp = await axios.post(`${API}/raw-materials`, {
        category: selectedAddCategory,
        category_data: cdata,
        low_stock_threshold: lowStockThreshold,
        uom: addUom || undefined,
        source_type: isInm ? inmSourceType : (addSourceType || undefined),
        parent_rm_id: isInm && inmParentRmId ? inmParentRmId.toUpperCase() : undefined,
        dual_uom: addDualUom,
        procurement_uom: addDualUom ? addProcurementUom : undefined,
        consumption_uom: addDualUom ? addConsumptionUom : undefined,
        conversion_factor: addDualUom && addConversionFactor ? parseFloat(addConversionFactor) : undefined
      });
      toast.success("Raw material added successfully");
      // Jul 2026 — surface INM BOM-derive skip reason. The RM was
      // created (200) but if `enrich_inm_l2_bom` couldn't write the
      // rm_bom the reason arrives in `response.warnings[]` + a
      // structured `response.inm_bom` object. Stable reason
      // vocabulary — DO NOT paraphrase to strings other than the
      // whitelist below without updating the backend contract.
      const _inmBom = _addResp?.data?.inm_bom;
      const _warnings = _addResp?.data?.warnings || [];
      if (_inmBom && _inmBom.bom_written === false && _inmBom.reason) {
        const _reasonMap = {
          purchased_no_consuming_bom: "PURCHASED INM — no consuming BOM written (by design).",
          no_inmfab_link: "No INMFAB parent set — BOM not derived. Link the INMFAB to derive the PWD recipe.",
          parent_not_inmfab: "parent_rm_id points at a non-INMFAB row — BOM not derived.",
          inmfab_missing_dosage: "INMFAB has no dosage set (powder_qty_grams) — BOM not derived. Set the INMFAB dosage and re-run enrichment.",
          no_preferred_pwd_for_colourant: "No PREFERRED PWD linked to this colourant — BOM not derived. Promote a PWD to PREFERRED on the colourant.",
        };
        const _msg = _reasonMap[_inmBom.reason] || `BOM not derived: ${_inmBom.reason}`;
        toast.warning(_msg, { duration: 10000 });
      } else if (_warnings.length > 0) {
        // Any residual warning that didn't come with a structured
        // inm_bom (e.g. enrich_error) — still surface it.
        toast.warning(_warnings.join("; "), { duration: 10000 });
      }
      setShowAddDialog(false);
      resetAddForm();
      fetchMaterials();
    } catch (error) {
      // Dedup (Feb 2026): backend returns 409 with structured detail when an
      // identical RM (same category + same category_data fields) already exists.
      if (error.response?.status === 409 && error.response?.data?.detail?.duplicate) {
        const d = error.response.data.detail;
        toast.warning(
          `Already exists as ${d.existing_rm_id}${d.existing_description ? ` — ${d.existing_description}` : ""}`,
          { duration: 6000 }
        );
        setShowAddDialog(false);
        resetAddForm();
        // Pre-filter the table to the matched RM so the user can see it immediately.
        setFilters(prev => ({ ...prev, search: d.existing_rm_id }));
        return;
      }
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Failed to add raw material"));
    }
  };

  const resetAddForm = () => {
    setSelectedAddCategory("");
    setCategoryData({});
    setLowStockThreshold(10);
    setAddUom("");
    setAddSourceType("");
    setAddDualUom(false);
    setAddProcurementUom("");
    setAddConsumptionUom("");
    setAddConversionFactor("");
    // Phase B.6 — clear mould-driven state
    setMouldSearchTerm("");
    setMouldOptions([]);
    setSelectedMould(null);
    setSelectedColourantId("");
    setInpPantoneCode("");
    setInpMbRmId("");
    setInpPreview(null);
    setInpPreviewLoading(false);
    setInmPwdRmId("");
    // Feb 2026 (Commit 2) — INMFAB dosage + INM parent state reset.
    setInmfabPowderQtyGrams("");
    setInmfabCoatingScrapFactor("0.10");
    setInmfabDescription("");
    setInmParentRmId("");
    setInmParentSearchTerm("");
    setInmParentOptions([]);
    setInmSourceType("MANUFACTURED");
  };

  // Feb 2026 (Commit 2) — INMFAB typeahead for the INM parent picker.
  // Debounced search against `/raw-materials/search` filtered to
  // category=INMFAB via a client-side pass (search endpoint doesn't
  // support category filter directly).
  useEffect(() => {
    if (selectedAddCategory !== "INM") return;
    const term = inmParentSearchTerm.trim();
    if (!term || term.length < 2) {
      setInmParentOptions([]);
      return;
    }
    let cancelled = false;
    setInmParentLoading(true);
    const handle = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/raw-materials/search`, {
          params: { q: term, limit: 25 },
        });
        if (cancelled) return;
        const items = (r.data?.items || []).filter(
          (i) => (i.category || "").toUpperCase() === "INMFAB"
        );
        setInmParentOptions(items);
      } catch {
        if (!cancelled) setInmParentOptions([]);
      } finally {
        if (!cancelled) setInmParentLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [inmParentSearchTerm, selectedAddCategory]);

  // Phase B.6 — Mould typeahead (debounced search against /api/moulds)
  useEffect(() => {
    if (selectedAddCategory !== "INP") return;
    const term = mouldSearchTerm.trim();
    if (!term) {
      setMouldOptions([]);
      return;
    }
    let cancelled = false;
    setMouldSearchLoading(true);
    const handle = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/moulds`, { params: { search: term, status: "ACTIVE" } });
        if (!cancelled) setMouldOptions((r.data || []).slice(0, 25));
      } catch {
        if (!cancelled) setMouldOptions([]);
      } finally {
        if (!cancelled) setMouldSearchLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [mouldSearchTerm, selectedAddCategory]);

  // Phase B.6 — Load Colourant SKUs for the INP + INM pickers when the
  // user opens the dialog. Filter by application=INJECTION.
  useEffect(() => {
    if (!showAddDialog) return;
    if (selectedAddCategory !== "INP" && selectedAddCategory !== "INM") return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/colourway/colourant-skus`, {
          params: { application: "INJECTION", status: "ACTIVE", limit: 500 },
        });
        if (cancelled) return;
        const items = r.data?.items || (Array.isArray(r.data) ? r.data : []);
        if (selectedAddCategory === "INP") setInpColourantOptions(items);
        // Jul 2026: INM colourant picker was removed. Leave hook in place
        // as a no-op so the caller doesn't need branching. INP still uses
        // its own colourant options.
        if (selectedAddCategory === "INM") { /* no-op: pantone-driven */ }
      } catch {
        if (selectedAddCategory === "INP") setInpColourantOptions([]);
        if (selectedAddCategory === "INM") { /* no-op: pantone-driven */ }
      }
    })();
    return () => { cancelled = true; };
  }, [showAddDialog, selectedAddCategory]);

  // Phase B.6 — Live BOM preview whenever the user picks/changes mould or colourant
  useEffect(() => {
    if (selectedAddCategory !== "INP" || !selectedMould || !selectedColourantId) {
      // Feb 2026 — preview-by-pantone isn't supported by the
      // `/raw-materials/from-mould/preview` endpoint (it takes a real
      // colourant_id). The new pantone flow skips the preview pane;
      // duplicate detection still fires on submit via the 409 response.
      setInpPreview(null);
      return;
    }
    let cancelled = false;
    setInpPreviewLoading(true);
    (async () => {
      try {
        const r = await axios.get(`${API}/raw-materials/from-mould/preview`, {
          params: { mould_id: selectedMould.id, colourant_id: selectedColourantId },
        });
        if (!cancelled) setInpPreview(r.data);
      } catch (e) {
        if (!cancelled) {
          setInpPreview(null);
          toast.error(e.response?.data?.detail || "Preview failed");
        }
      } finally {
        if (!cancelled) setInpPreviewLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedAddCategory, selectedMould, selectedColourantId]);

  // Phase B.6 — Submit mould-driven INP create.
  // Feb 2026: switched from `colourant_id` payload to `(pantone_code,
  // mb_rm_id)` so the backend can find-or-mint a colourant on the
  // de-parted (polymer, pantone, INJECTION) identity. Operators don't
  // pre-create colourants any more.
  const handleAddInpFromMould = async () => {
    if (!selectedMould || !inpPantoneCode) {
      toast.error("Pick a mould and enter a Pantone code");
      return;
    }
    try {
      const r = await axios.post(`${API}/raw-materials/from-mould`, {
        mould_id: selectedMould.id,
        pantone_code: inpPantoneCode.trim(),
        mb_rm_id: inpMbRmId || undefined,
        low_stock_threshold: lowStockThreshold,
      });
      const mintedNote = r.data.colourant_minted ? " (new Colourant SKU minted)" : "";
      const msg = r.data.reactivated
        ? `Re-activated INP ${r.data.rm?.rm_id}${mintedNote}`
        : `Created INP ${r.data.rm?.rm_id}${mintedNote} (${r.data.bom_components?.length || 0} BOM rows)`;
      toast.success(msg);
      setShowAddDialog(false);
      resetAddForm();
      fetchMaterials();
    } catch (e) {
      if (e.response?.status === 409 && e.response?.data?.detail?.duplicate) {
        const d = e.response.data.detail;
        toast.warning(
          `Already exists as ${d.existing_rm_id}${d.existing_description ? ` — ${d.existing_description}` : ""}`,
          { duration: 6000 }
        );
        setShowAddDialog(false);
        resetAddForm();
        setFilters(prev => ({ ...prev, search: d.existing_rm_id }));
        return;
      }
      const detail = e.response?.data?.detail;
      const code = detail?.code;
      if (code === "PANTONE_NOT_IN_MASTER") {
        toast.error(detail.message || "Pantone not in master library. Add it via Tech Ops → Pantones first.");
        return;
      }
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Failed to create INP"));
    }
  };

  const downloadCategoryTemplate = (category) => {
    if (!rmCategories[category]) {
      toast.error("Category not found");
      return;
    }
    // Prefer description_columns (carries human-readable labels) and fall
    // back to the raw `fields` array for older category records.
    const descCols = rmCategories[category].description_columns || [];
    const rawKeys = rmCategories[category].fields || [];
    const fieldSpecs = descCols.length > 0
      ? descCols.map(c => ({ key: c.key, label: c.label || c.key }))
      : rawKeys.map(k => ({ key: k, label: k }));

    // If any field looks like a Pantone code (`pantone`, `pantone_code`,
    // `pantone_shade`), append optional helper columns so users can seed
    // the Pantone library inline. Honoured by `services/pantone_autosync.py`.
    const hasPantone = fieldSpecs.some(f => /pantone/i.test(String(f.key)));
    const extraPantoneCols = hasPantone ? ['Pantone Name', 'Color Hex'] : [];

    const headers = ['Category', ...fieldSpecs.map(f => f.label), ...extraPantoneCols, 'Low Stock Threshold'];
    const sampleRow = [
      category,
      ...fieldSpecs.map(() => ''),
      ...extraPantoneCols.map(() => ''),
      10,
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers, sampleRow]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, category);
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), `${category}_template.xlsx`);
    toast.success(
      hasPantone
        ? `Downloaded ${category} template (Pantone Name + Color Hex optional — fill them to auto-seed the Pantone library)`
        : `Downloaded ${category} template`,
      { duration: hasPantone ? 6000 : 3000 }
    );
  };

  const handleMigrateImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.json')) {
      toast.error('Please upload a JSON file (exported from migration)');
      e.target.value = null;
      return;
    }
    
    setMigrating(true);
    toast.info(`Importing ${file.name}... This may take a moment.`);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/raw-materials/migrate/import`, formData, {
        timeout: 120000
      });
      
      const { results, totals, message, success } = response.data;
      
      if (success === false) {
        toast.error(message || 'Import failed - check file format');
      } else if (results.raw_materials.imported > 0) {
        toast.success(
          `Import complete! ${results.raw_materials.imported} RMs added, ${results.raw_materials.skipped} duplicates skipped. Total: ${totals.raw_materials}`,
          { duration: 8000 }
        );
        fetchMaterials();
      } else {
        toast.warning(message || 'No new items imported. Items may already exist.');
      }
      setShowMigrateDialog(false);
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        toast.warning('Import is taking longer than expected. It may still complete - refresh to check.', { duration: 10000 });
      } else {
        const errMsg = error.response?.data?.message || error.response?.data?.detail || 'Import failed';
        toast.error(errMsg, { duration: 8000 });
      }
    }
    setMigrating(false);
    e.target.value = null;
  };

  const handleDeleteRM = async (rmId) => {
    if (!window.confirm(`Delete ${rmId}? This action cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/raw-materials/${rmId}`);
      toast.success("Raw material deleted");
      fetchMaterials();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  // Open the vendors-for-RM lookup dialog. Pulls all vendor_rm_prices
  // rows for the RM via the flat list endpoint. Read-only — edits stay
  // in Vendor Management.
  const openVendorsForRM = async (rm) => {
    setVendorsForRMTarget(rm);
    setVendorsForRM([]);
    setShowVendorsDialog(true);
    setVendorsForRMLoading(true);
    try {
      const resp = await axios.get(`${API}/vendor-rm-prices`, {
        params: { rm_id: rm.rm_id, limit: 200 },
      });
      // Endpoint returns a flat array of
      // {id, vendor_id, vendor_name, rm_id, price, currency, updated_at}
      const list = Array.isArray(resp.data)
        ? resp.data
        : (resp.data?.prices || resp.data?.results || []);
      setVendorsForRM(list);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load vendors for this RM");
    } finally {
      setVendorsForRMLoading(false);
    }
  };

  const [backfilling, setBackfilling] = useState(false);
  
  const handleBackfillDescriptions = async () => {
    if (!window.confirm("This will regenerate descriptions for ALL RMs using the latest format. Continue?")) {
      return;
    }
    
    setBackfilling(true);
    try {
      // First sync category formats to ensure correct configuration
      toast.info("Syncing category formats...");
      await axios.post(`${API}/raw-materials/sync-category-formats`);
      
      // Then run backfill with force mode
      toast.info("Starting description backfill (force mode)...");
      const response = await axios.post(`${API}/raw-materials/backfill-descriptions?force=true`);
      const { updated, skipped, message } = response.data;
      toast.success(message || `Updated ${updated} RMs, skipped ${skipped}`, { duration: 5000 });
      fetchMaterials(); // Refresh to show new descriptions
    } catch (error) {
      toast.error(error.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  const formatFieldName = (field) => {
    return field.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  return (
    <div className="p-6 space-y-6" data-testid="rm-repository-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">RM Repository</h1>
          <p className="text-gray-500 mt-1">Manage raw material master data, tags, and approve RM requests</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Hidden file inputs */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleBulkUpload}
            accept=".xlsx,.xls"
            className="hidden"
            data-testid="bulk-upload-input"
          />
          <input
            type="file"
            ref={uomPatchInputRef}
            onChange={(e) => handleBulkUpload(e, { updateExisting: true })}
            accept=".xlsx,.xls"
            className="hidden"
            data-testid="bulk-update-uom-input"
          />
          
          {canManageRMs && (
            <>
              <PermButton 
                entity="RawMaterial" action="CREATE"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-2"
                data-testid="bulk-upload-btn"
              >
                <Upload className="h-4 w-4" />
                Bulk Upload
              </PermButton>
              <PermButton
                entity="RawMaterial" action="UPDATE"
                variant="outline"
                onClick={() => uomPatchInputRef.current?.click()}
                className="flex items-center gap-2"
                data-testid="bulk-update-uom-btn"
                title="Patch Dual UOM / Procurement UOM / Consumption UOM / Conversion Factor on existing RMs by RM Code"
              >
                <Upload className="h-4 w-4" />
                Update UOMs
              </PermButton>
              <PermButton 
                entity="RawMaterial" action="CREATE"
                onClick={() => setShowAddDialog(true)}
                className="flex items-center gap-2"
                data-testid="add-rm-btn"
              >
                <Plus className="h-4 w-4" />
                Add RM
              </PermButton>
            </>
          )}
          {isAdmin && (
            <Button 
              onClick={handleBackfillDescriptions}
              variant="outline"
              disabled={backfilling}
              className="flex items-center gap-2"
              data-testid="backfill-descriptions-btn"
            >
              <Database className="h-4 w-4" />
              {backfilling ? "Backfilling..." : "Backfill Descriptions"}
            </Button>
          )}
          <Button 
            onClick={handleExportRepository}
            variant="outline"
            className="flex items-center gap-2"
            data-testid="export-rm-repository-btn"
          >
            <Download className="h-4 w-4" />
            Export All RMs
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total RMs</p>
                <p className="text-2xl font-bold">{materials.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Tag className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Tagged RMs</p>
                <p className="text-2xl font-bold">
                  {materials.filter(m => (m.brand_ids?.length > 0 || m.vertical_ids?.length > 0)).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Layers className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Brand Specific</p>
                <p className="text-2xl font-bold">
                  {materials.filter(m => m.is_brand_specific).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={pendingCount > 0 ? "border-orange-300 bg-orange-50" : ""}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${pendingCount > 0 ? "bg-orange-100" : "bg-gray-100"}`}>
                <Bell className={`h-5 w-5 ${pendingCount > 0 ? "text-orange-600" : "text-gray-600"}`} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Pending Requests</p>
                <p className="text-2xl font-bold">{pendingCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="repository" data-testid="repository-tab">
            <Package className="h-4 w-4 mr-2" />
            RM Repository
          </TabsTrigger>
          <TabsTrigger value="requests" data-testid="requests-tab">
            <Bell className="h-4 w-4 mr-2" />
            RM Requests
            {pendingCount > 0 && (
              <Badge className="ml-2 bg-orange-500">{pendingCount}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="sku-requests" data-testid="sku-requests-tab">
            <Box className="h-4 w-4 mr-2" />
            Buyer SKU Requests
            {pendingSkuCount > 0 && (
              <Badge className="ml-2 bg-blue-500">{pendingSkuCount}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="price-history" data-testid="price-history-tab">
            <Database className="h-4 w-4 mr-2" />
            Price History
          </TabsTrigger>
          <TabsTrigger value="price-master" data-testid="price-master-tab">
            <Database className="h-4 w-4 mr-2" />
            Price Master
          </TabsTrigger>
        </TabsList>

        {/* Repository Tab */}
        <TabsContent value="repository" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-xs text-gray-500">Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search RM ID or name..."
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                      onKeyDown={(e) => e.key === 'Enter' && fetchMaterials()}
                      className="pl-10"
                      data-testid="rm-search"
                    />
                  </div>
                </div>
                
                <div className="w-[150px]">
                  <Label className="text-xs text-gray-500">Category</Label>
                  <Select value={filters.category} onValueChange={(v) => handleFilterChange('category', v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="filter-category">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {Object.entries(rmCategories).map(([code, cat]) => (
                        <SelectItem key={code} value={code}>{code} - {cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="w-[180px]">
                  <Label className="text-xs text-gray-500">Brand</Label>
                  <Select value={filters.brand_id} onValueChange={(v) => handleFilterChange('brand_id', v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="filter-brand">
                      <SelectValue placeholder="All Brands" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Brands</SelectItem>
                      {brands.map(b => (
                        <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="w-[180px]">
                  <Label className="text-xs text-gray-500">Vertical</Label>
                  <Select value={filters.vertical_id} onValueChange={(v) => { handleFilterChange('vertical_id', v === "all" ? "" : v); setFilters(prev => ({ ...prev, model_id: "" })); }}>
                    <SelectTrigger data-testid="filter-vertical">
                      <SelectValue placeholder="All Verticals" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Verticals</SelectItem>
                      {verticals.map(v => (
                        <SelectItem key={v.id} value={v.id}>{v.code} - {v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="w-[180px]">
                  <Label className="text-xs text-gray-500">Model</Label>
                  <Select value={filters.model_id} onValueChange={(v) => handleFilterChange('model_id', v === "all" ? "" : v)} disabled={!filters.vertical_id}>
                    <SelectTrigger data-testid="filter-model">
                      <SelectValue placeholder="All Models" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Models</SelectItem>
                      {getFilteredModels().map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.code} - {m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="w-[150px]">
                  <Label className="text-xs text-gray-500">Source Type</Label>
                  <Select value={filters.source_type || "all"} onValueChange={(v) => handleFilterChange('source_type', v === "all" ? "" : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="PURCHASED">PURCHASED</SelectItem>
                      <SelectItem value="MANUFACTURED">MANUFACTURED</SelectItem>
                      <SelectItem value="BOTH">BOTH</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="w-[120px]">
                  <Label className="text-xs text-gray-500">BOM Level</Label>
                  <Select value={filters.bom_level || "all"} onValueChange={(v) => handleFilterChange('bom_level', v === "all" ? "" : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Levels" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Levels</SelectItem>
                      <SelectItem value="1">L1</SelectItem>
                      <SelectItem value="2">L2</SelectItem>
                      <SelectItem value="3">L3</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="w-[150px]">
                  <Label className="text-xs text-gray-500">Brand Specific</Label>
                  <Select value={filters.is_brand_specific || "all"} onValueChange={(v) => handleFilterChange('is_brand_specific', v === "all" ? "" : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="true">Yes</SelectItem>
                      <SelectItem value="false">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs text-gray-500">Pantone</Label>
                  <Input
                    list="rm-pantone-list"
                    placeholder="e.g. 485 C"
                    value={filters.pantone_code}
                    onChange={(e) => handleFilterChange('pantone_code', e.target.value)}
                    className="font-mono"
                    data-testid="rm-filter-pantone"
                  />
                  <datalist id="rm-pantone-list">
                    {pantoneShadesMaster.map((p) => (
                      <option
                        key={p.pantone_code}
                        value={p.pantone_code}
                        label={p.pantone_name ? `${p.pantone_code} — ${p.pantone_name}` : p.pantone_code}
                      />
                    ))}
                  </datalist>
                </div>

                <div>
                  <Label className="text-xs text-gray-500">Colourant SKU</Label>
                  <Input
                    list="rm-colourant-list"
                    placeholder="CLR_..."
                    value={filters.colourant_id}
                    onChange={(e) => handleFilterChange('colourant_id', e.target.value)}
                    className="font-mono"
                    data-testid="rm-filter-colourant"
                  />
                  <datalist id="rm-colourant-list">
                    {colourantSkusMaster.map((c) => (
                      <option
                        key={c.colourant_id}
                        value={c.colourant_id}
                        label={`${c.colourant_id} — ${c.part_id || ""} / ${c.pantone_code || ""}`}
                      />
                    ))}
                  </datalist>
                </div>

                <Button variant="outline" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-2" />
                  Clear
                </Button>
                
                <Button onClick={fetchMaterials}>
                  <Search className="h-4 w-4 mr-2" />
                  Apply
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Bulk Actions */}
          {selectedRMs.size > 0 && (
            <div className="flex items-center gap-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <span className="text-sm font-medium text-blue-700">
                {selectedRMs.size} RM(s) selected
              </span>
              <Button size="sm" onClick={() => {
                setTagForm({ brand_ids: [], vertical_ids: [], model_ids: [], is_brand_specific: false });
                setShowBulkTagDialog(true);
              }}>
                <Tag className="h-4 w-4 mr-2" />
                Bulk Add Tags
              </Button>
              <Button size="sm" variant="outline" onClick={() => setSelectedRMs(new Set())}>
                Clear Selection
              </Button>
            </div>
          )}

          {/* Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">
                      <Checkbox 
                        checked={selectAll}
                        onCheckedChange={handleSelectAll}
                        data-testid="select-all"
                      />
                    </TableHead>
                    <TableHead>RM ID</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead>Brands</TableHead>
                    <TableHead>Verticals</TableHead>
                    <TableHead>Models</TableHead>
                    <TableHead>Brand Specific</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center py-8 text-gray-500">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : materials.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center py-8 text-gray-500">
                        No raw materials found
                      </TableCell>
                    </TableRow>
                  ) : (
                    materials.map(rm => (
                      <TableRow key={rm.rm_id} data-testid={`rm-row-${rm.rm_id}`}>
                        <TableCell>
                          <Checkbox 
                            checked={selectedRMs.has(rm.rm_id)}
                            onCheckedChange={() => handleSelectRM(rm.rm_id)}
                          />
                        </TableCell>
                        <TableCell>
                          <span className="font-mono font-medium">{rm.rm_id}</span>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{rm.category}</Badge>
                        </TableCell>
                        <TableCell className="max-w-[250px]">
                          <div className="flex flex-col gap-0.5">
                            <span className="truncate block" title={rm.description || rm.category_data?.name || "-"}>
                              {rm.description || rm.category_data?.name || "-"}
                            </span>
                            {(rm.category || "").toUpperCase() === "INMFAB" && (
                              <div className="flex items-center gap-1 flex-wrap" data-testid={`inmfab-dosage-row-${rm.rm_id}`}>
                                <Badge variant="outline" className="text-[10px] bg-emerald-50 border-emerald-200 text-emerald-800 font-mono">
                                  <Beaker className="w-2.5 h-2.5 mr-1" />
                                  {rm.category_data?.powder_qty_grams ?? "?"} g
                                </Badge>
                                <Badge variant="outline" className="text-[10px] bg-emerald-50 border-emerald-200 text-emerald-800 font-mono" title="Coating scrap factor">
                                  ×{(1 + (Number(rm.category_data?.coating_scrap_factor) || 0)).toFixed(2)}
                                </Badge>
                                <Badge
                                  variant="outline"
                                  className="text-[10px] bg-blue-50 border-blue-200 text-blue-800"
                                  data-testid={`inmfab-linked-count-${rm.rm_id}`}
                                  title="Linked INMs (all source types)"
                                >
                                  <Users className="w-2.5 h-2.5 mr-1" />
                                  {linkedInmCounts[(rm.rm_id || "").toUpperCase()] || 0} INM
                                </Badge>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const eff = rm.effective_source_type;
                            if (!eff) {
                              return (
                                <Badge
                                  variant="outline"
                                  className="bg-red-50 text-red-700 border-red-300"
                                  title={`Category ${rm.category || ""} has no default_source_type configured. Set it under Master Data → Categories.`}
                                  data-testid={`source-type-unconfigured-${rm.rm_id}`}
                                >
                                  Unconfigured
                                </Badge>
                              );
                            }
                            return (
                              <Badge
                                variant={eff === 'MANUFACTURED' ? 'default' : eff === 'BOTH' ? 'secondary' : 'outline'}
                                className={eff === 'MANUFACTURED' ? 'bg-orange-500' : eff === 'BOTH' ? 'bg-blue-100 text-blue-700' : ''}
                                title={`From category ${rm.category}`}
                              >
                                {eff}
                              </Badge>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          {rm.bom_level ? (
                            <Badge variant="outline" className="font-mono">
                              L{rm.bom_level}
                            </Badge>
                          ) : (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(rm.brands || []).slice(0, 3).map(b => (
                              <Badge key={b.code} variant="secondary" className="text-xs">
                                {b.code}
                              </Badge>
                            ))}
                            {(rm.brands || []).length > 3 && (
                              <Badge variant="secondary" className="text-xs">
                                +{rm.brands.length - 3}
                              </Badge>
                            )}
                            {(!rm.brands || rm.brands.length === 0) && (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(rm.verticals || []).slice(0, 2).map(v => (
                              <Badge key={v.code} variant="outline" className="text-xs bg-blue-50">
                                {v.code}
                              </Badge>
                            ))}
                            {(rm.verticals || []).length > 2 && (
                              <Badge variant="outline" className="text-xs">
                                +{rm.verticals.length - 2}
                              </Badge>
                            )}
                            {(!rm.verticals || rm.verticals.length === 0) && (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(rm.models || []).slice(0, 2).map(m => (
                              <Badge key={m.code} variant="outline" className="text-xs bg-green-50">
                                {m.code}
                              </Badge>
                            ))}
                            {(rm.models || []).length > 2 && (
                              <Badge variant="outline" className="text-xs">
                                +{rm.models.length - 2}
                              </Badge>
                            )}
                            {(!rm.models || rm.models.length === 0) && (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {rm.is_brand_specific ? (
                            <Badge className="bg-purple-100 text-purple-700">Yes</Badge>
                          ) : (
                            <span className="text-gray-400">No</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openVendorsForRM(rm)}
                              title="View vendors tagged to this RM with prices"
                              data-testid={`view-vendors-${rm.rm_id}`}
                            >
                              <Users className="h-4 w-4" />
                            </Button>
                            <PermButton 
                              entity="RawMaterial" action="UPDATE"
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleEditTags(rm)}
                              data-testid={`edit-tags-${rm.rm_id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </PermButton>
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setConvertTargetRm(rm)}
                                title="Convert this RM to a different category (reversible)"
                                data-testid={`convert-category-${rm.rm_id}`}
                              >
                                <span className="text-[10px] font-medium tracking-wider text-amber-700">
                                  CVT
                                </span>
                              </Button>
                            )}
                            {(rm.category || "").toUpperCase() === "INMFAB" && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost" size="sm"
                                    title="INMFAB actions"
                                    data-testid={`inmfab-actions-${rm.rm_id}`}
                                  >
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuLabel className="text-[10px] text-muted-foreground">
                                    {linkedInmCounts[(rm.rm_id || "").toUpperCase()] || 0} linked INM(s)
                                  </DropdownMenuLabel>
                                  <DropdownMenuSeparator />
                                  {isAdmin ? (
                                    <DropdownMenuItem
                                      onClick={() => openDosageDialog(rm)}
                                      data-testid={`inmfab-edit-dosage-${rm.rm_id}`}
                                    >
                                      <Beaker className="w-4 h-4 mr-2" /> Edit dosage (cascade)
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem disabled>
                                      <Beaker className="w-4 h-4 mr-2" /> Edit dosage — master-admin only
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {/* Pagination */}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                loading={loading}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* RM Requests Tab */}
        <TabsContent value="requests" className="space-y-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Requested Name</TableHead>
                    <TableHead>For Brands</TableHead>
                    <TableHead>Buyer SKU</TableHead>
                    <TableHead>Requested By</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : rmRequests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                        No RM requests
                      </TableCell>
                    </TableRow>
                  ) : (
                    rmRequests.map(req => (
                      <TableRow key={req.id} data-testid={`request-row-${req.id}`}>
                        <TableCell>
                          {req.status === "PENDING" && (
                            <Badge className="bg-orange-100 text-orange-700">
                              <Clock className="h-3 w-3 mr-1" />
                              Pending
                            </Badge>
                          )}
                          {req.status === "APPROVED" && (
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Approved
                            </Badge>
                          )}
                          {req.status === "REJECTED" && (
                            <Badge className="bg-red-100 text-red-700">
                              <XCircle className="h-3 w-3 mr-1" />
                              Rejected
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{req.category}</Badge>
                        </TableCell>
                        <TableCell className="max-w-[200px]">
                          <span className="font-medium">{req.requested_name}</span>
                          {req.description && (
                            <p className="text-xs text-gray-500 truncate">{req.description}</p>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(req.brands || []).map(b => (
                              <Badge key={b.code} variant="secondary" className="text-xs">
                                {b.code}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          {req.buyer_sku_id ? (
                            <span className="font-mono text-xs">{req.buyer_sku_id}</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {req.requester_name || "Unknown"}
                        </TableCell>
                        <TableCell className="text-xs text-gray-500">
                          {new Date(req.requested_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {req.status === "PENDING" ? (
                            <div className="flex gap-1">
                              <Button 
                                size="sm" 
                                variant="default"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => handleReviewRequest(req.id, "APPROVE")}
                                data-testid={`approve-${req.id}`}
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => handleReviewRequest(req.id, "REJECT")}
                                data-testid={`reject-${req.id}`}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="text-xs text-gray-500">
                              {req.created_rm_id && (
                                <span className="font-mono">{req.created_rm_id}</span>
                              )}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Buyer SKU Requests Tab */}
        <TabsContent value="sku-requests" className="space-y-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Buyer SKU</TableHead>
                    <TableHead>Base SKU</TableHead>
                    <TableHead>Brand</TableHead>
                    <TableHead>Requested By</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : buyerSkuRequests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                        No Buyer SKU requests
                      </TableCell>
                    </TableRow>
                  ) : (
                    buyerSkuRequests.map(req => (
                      <TableRow key={req.id} data-testid={`sku-request-row-${req.id}`}>
                        <TableCell>
                          {req.status === "PENDING" && (
                            <Badge className="bg-orange-100 text-orange-700">
                              <Clock className="h-3 w-3 mr-1" />
                              Pending
                            </Badge>
                          )}
                          {req.status === "APPROVED" && (
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Approved
                            </Badge>
                          )}
                          {req.status === "REJECTED" && (
                            <Badge className="bg-red-100 text-red-700">
                              <XCircle className="h-3 w-3 mr-1" />
                              Rejected
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="font-mono font-medium">{req.buyer_sku_id}</span>
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-sm text-gray-600">{req.bidso_sku_id}</span>
                          {req.bidso_sku_name && (
                            <p className="text-xs text-gray-400">{req.bidso_sku_name}</p>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{req.brand_code} - {req.brand_name}</Badge>
                        </TableCell>
                        <TableCell>
                          {req.requester_name || "Unknown"}
                        </TableCell>
                        <TableCell className="text-xs text-gray-500">
                          {new Date(req.requested_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="max-w-[150px]">
                          <span className="text-xs text-gray-500 truncate">{req.notes || "-"}</span>
                        </TableCell>
                        <TableCell>
                          {req.status === "PENDING" ? (
                            <div className="flex gap-1">
                              <Button 
                                size="sm" 
                                variant="default"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => handleReviewBuyerSkuRequest(req.id, "APPROVE")}
                                data-testid={`approve-sku-${req.id}`}
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => handleReviewBuyerSkuRequest(req.id, "REJECT")}
                                data-testid={`reject-sku-${req.id}`}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-500">
                              {req.review_notes || "-"}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Price History Tab */}
        <TabsContent value="price-history" className="space-y-4">
          {/* Upload + Stats */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Upload className="h-4 w-4" /> Upload RM Prices
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-muted-foreground">
                Expected columns: <span className="font-mono">Date | Invoice No | Vendor ID | RM ID | Price</span>.
                Price is per unit. System computes a simple 3-month rolling average per RM and uses it to derive BOM cost.
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Mode</Label>
                  <div
                    className="h-9 px-3 py-1.5 text-xs border rounded bg-zinc-50 text-zinc-600 flex items-center"
                    data-testid="price-upload-mode-locked"
                    title="Overwrite All was permanently removed after a data-loss incident — uploads always append, per-invoice duplicates are rejected and surfaced in the receipt."
                  >
                    Append (locked)
                  </div>
                </div>
                <Input
                  ref={priceFileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handlePriceUpload}
                  disabled={priceUploading}
                  className="max-w-xs text-xs"
                  data-testid="price-upload-input"
                />
                <Button variant="outline" size="sm" onClick={downloadPriceTemplate} data-testid="download-price-template">
                  <Download className="h-4 w-4 mr-1" /> Template
                </Button>
              </div>
              {priceStats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
                  <div className="border rounded p-3 bg-zinc-50">
                    <div className="text-xs text-muted-foreground">Total Records</div>
                    <div className="text-xl font-semibold">{priceStats.total_records?.toLocaleString()}</div>
                  </div>
                  <div className="border rounded p-3 bg-zinc-50">
                    <div className="text-xs text-muted-foreground">Unique RMs</div>
                    <div className="text-xl font-semibold">{priceStats.unique_rms?.toLocaleString()}</div>
                  </div>
                  <div className="border rounded p-3 bg-zinc-50">
                    <div className="text-xs text-muted-foreground">Unique Vendors</div>
                    <div className="text-xl font-semibold">{priceStats.unique_vendors}</div>
                  </div>
                  <div className="border rounded p-3 bg-emerald-50">
                    <div className="text-xs text-muted-foreground">RMs with Avg Price</div>
                    <div className="text-xl font-semibold text-emerald-700">{priceStats.rms_with_avg_price}</div>
                  </div>
                </div>
              )}
              {priceStats?.date_range?.min && (
                <div className="text-xs text-muted-foreground">
                  Data range: {priceStats.date_range.min?.slice(0,10)} → {priceStats.date_range.max?.slice(0,10)} ·
                  Rolling window start: {priceStats.window_start?.slice(0,10)}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Avg Prices Table */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Average Prices (3-Month Rolling)</CardTitle>
            </CardHeader>
            <CardContent>
              {avgPrices.length === 0 ? (
                <div className="text-center text-sm text-muted-foreground py-6">
                  No price data yet. Upload an Excel file to get started.
                </div>
              ) : (
                <div className="overflow-x-auto max-h-96 border rounded">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-zinc-50 sticky top-0">
                        <TableHead className="text-xs">RM ID</TableHead>
                        <TableHead className="text-xs">Name</TableHead>
                        <TableHead className="text-xs">Category</TableHead>
                        <TableHead className="text-xs text-right">Avg Price</TableHead>
                        <TableHead className="text-xs text-right">Records</TableHead>
                        <TableHead className="text-xs">Latest Invoice</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {avgPrices.map((r) => (
                        <TableRow key={r.rm_id}>
                          <TableCell className="font-mono text-xs">{r.rm_id}</TableCell>
                          <TableCell className="text-xs">{r.rm_name || "-"}</TableCell>
                          <TableCell className="text-xs">{r.category || "-"}</TableCell>
                          <TableCell className="text-xs text-right font-mono text-emerald-700">
                            Rs {r.avg_price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                          </TableCell>
                          <TableCell className="text-xs text-right">{r.record_count}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {r.latest_date?.slice(0,10)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Price History Log */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-base">Invoice History</CardTitle>
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Filter by RM ID"
                    value={priceFilter.rm_id}
                    onChange={(e) => setPriceFilter({ ...priceFilter, rm_id: e.target.value })}
                    className="h-8 text-xs w-40"
                    data-testid="price-filter-rm"
                  />
                  <Input
                    placeholder="Filter by Vendor ID"
                    value={priceFilter.vendor_id}
                    onChange={(e) => setPriceFilter({ ...priceFilter, vendor_id: e.target.value })}
                    className="h-8 text-xs w-40"
                    data-testid="price-filter-vendor"
                  />
                  <Button size="sm" variant="outline" onClick={() => fetchPriceHistory()}>Apply</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {priceHistory.length === 0 ? (
                <div className="text-center text-sm text-muted-foreground py-6">No history records.</div>
              ) : (
                <div className="overflow-x-auto max-h-96 border rounded">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-zinc-50 sticky top-0">
                        <TableHead className="text-xs">Date</TableHead>
                        <TableHead className="text-xs">Invoice No</TableHead>
                        <TableHead className="text-xs">Vendor</TableHead>
                        <TableHead className="text-xs">RM ID</TableHead>
                        <TableHead className="text-xs text-right">Price/Unit</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {priceHistory.map((r) => (
                        <TableRow key={r.id}>
                          <TableCell className="text-xs">{r.date?.slice(0,10)}</TableCell>
                          <TableCell className="text-xs font-mono">{r.invoice_no}</TableCell>
                          <TableCell className="text-xs" title={r.vendor_name}>
                            <span className="font-mono">{r.vendor_id}</span>
                            <span className="text-muted-foreground ml-1 truncate">· {r.vendor_name}</span>
                          </TableCell>
                          <TableCell className="text-xs font-mono">{r.rm_id}</TableCell>
                          <TableCell className="text-xs text-right font-mono">Rs {r.price_per_unit?.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============== PRICE MASTER TAB ============== */}
        <TabsContent value="price-master" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">RM Price Master</CardTitle>
              <p className="text-xs text-muted-foreground">
                Resolved price per RM across all categories — invoice → vendor map → rolled-up BOM. Missing prices show as &quot;MISSING&quot;.
              </p>
            </CardHeader>
            <CardContent>
              {/* Counter strip */}
              {priceMasterCounts && (
                <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4">
                  <div className="border rounded p-2 bg-zinc-50">
                    <div className="text-[10px] uppercase text-zinc-500">Total RMs</div>
                    <div className="text-lg font-mono font-semibold" data-testid="pm-total">{priceMasterCounts.total}</div>
                  </div>
                  <div className="border rounded p-2 bg-emerald-50">
                    <div className="text-[10px] uppercase text-emerald-700">Invoice</div>
                    <div className="text-lg font-mono font-semibold text-emerald-800" data-testid="pm-invoice">{priceMasterCounts.invoice}</div>
                  </div>
                  <div className="border rounded p-2 bg-amber-50">
                    <div className="text-[10px] uppercase text-amber-700">Stale invoice</div>
                    <div className="text-lg font-mono font-semibold text-amber-800" data-testid="pm-stale">{priceMasterCounts.stale_invoice ?? 0}</div>
                  </div>
                  <div className="border rounded p-2 bg-blue-50">
                    <div className="text-[10px] uppercase text-blue-700">Vendor map</div>
                    <div className="text-lg font-mono font-semibold text-blue-800" data-testid="pm-vendor-map">{priceMasterCounts.vendor_map}</div>
                  </div>
                  <div className="border rounded p-2 bg-purple-50">
                    <div className="text-[10px] uppercase text-purple-700">Rolled-up BOM</div>
                    <div className="text-lg font-mono font-semibold text-purple-800" data-testid="pm-rolled-up">{priceMasterCounts.rolled_up_bom}</div>
                  </div>
                  <div className="border rounded p-2 bg-red-50">
                    <div className="text-[10px] uppercase text-red-700">Missing</div>
                    <div className="text-lg font-mono font-semibold text-red-800" data-testid="pm-missing">{priceMasterCounts.MISSING}</div>
                  </div>
                </div>
              )}

              {/* Filter strip */}
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <Input
                  placeholder="Search RM ID / description"
                  value={priceMasterFilter.search}
                  onChange={e => setPriceMasterFilter(f => ({ ...f, search: e.target.value }))}
                  onKeyDown={e => e.key === "Enter" && applyPriceMasterFilter(priceMasterFilter)}
                  className="h-8 text-xs w-64"
                  data-testid="pm-search"
                />
                <Select value={priceMasterFilter.category || "ALL"} onValueChange={v => applyPriceMasterFilter({ ...priceMasterFilter, category: v === "ALL" ? "" : v })}>
                  <SelectTrigger className="h-8 w-32 text-xs" data-testid="pm-category"><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All categories</SelectItem>
                    {Object.keys(rmCategories || {}).sort().map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={priceMasterFilter.source || "ALL"} onValueChange={v => applyPriceMasterFilter({ ...priceMasterFilter, source: v === "ALL" ? "" : v })}>
                  <SelectTrigger className="h-8 w-40 text-xs" data-testid="pm-source"><SelectValue placeholder="Source" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All sources</SelectItem>
                    <SelectItem value="invoice">Invoice</SelectItem>
                    <SelectItem value="stale_invoice">Stale invoice</SelectItem>
                    <SelectItem value="vendor_map">Vendor map</SelectItem>
                    <SelectItem value="rolled_up_bom">Rolled-up BOM</SelectItem>
                    <SelectItem value="MISSING">Missing only</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={() => applyPriceMasterFilter(priceMasterFilter)} data-testid="pm-apply">Apply</Button>
                <Button size="sm" variant="outline" onClick={() => applyPriceMasterFilter({ category: "", source: "", search: "" })}>
                  Clear
                </Button>
                <div className="ml-auto">
                  <Button size="sm" onClick={exportPriceMaster} data-testid="pm-export">
                    <Download className="h-3.5 w-3.5 mr-1" /> Export Excel
                  </Button>
                </div>
              </div>

              {/* Table */}
              {priceMasterLoading ? (
                <div className="text-center text-sm text-muted-foreground py-8">Loading…</div>
              ) : priceMasterRows.length === 0 ? (
                <div className="text-center text-sm text-muted-foreground py-8">No rows match the filter.</div>
              ) : (
                <div className="overflow-x-auto max-h-[60vh] border rounded">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-zinc-50 sticky top-0">
                        <TableHead className="text-xs">RM ID</TableHead>
                        <TableHead className="text-xs">Name / Description</TableHead>
                        <TableHead className="text-xs">Category</TableHead>
                        <TableHead className="text-xs">UOM</TableHead>
                        <TableHead className="text-xs text-right">Avg Price</TableHead>
                        <TableHead className="text-xs">Source</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {priceMasterRows.map((r) => (
                        <TableRow
                          key={r.rm_id}
                          className={
                            r.source === "MISSING" ? "bg-red-50/40" :
                            r.source === "stale_invoice" ? "bg-amber-50/40" : ""
                          }
                          data-testid={`pm-row-${r.rm_id}`}
                        >
                          <TableCell className="text-xs font-mono">{r.rm_id}</TableCell>
                          <TableCell className="text-xs max-w-[400px] truncate" title={r.description || r.name}>{r.description || r.name}</TableCell>
                          <TableCell className="text-xs">{r.category || "-"}</TableCell>
                          <TableCell
                            className="text-xs cursor-help underline decoration-dotted underline-offset-2"
                            title={(() => {
                              const mu = r.master_uom || "";
                              if (r.is_dual_uom && r.conversion_factor && r.conversion_factor > 0) {
                                const cu = r.consumption_uom || "PCS";
                                return `${r.rm_id} · ${mu} (master) · BOM in ${cu} · 1 ${cu} = ${r.conversion_factor.toLocaleString(undefined, { maximumFractionDigits: 8 })} ${mu.toLowerCase()}`;
                              }
                              if (mu === "KG") {
                                return `${r.rm_id} · KG (master) · BOM in grams (legacy ÷1000) · 1000 g = 1 kg`;
                              }
                              return `${r.rm_id} · ${mu || "—"} (1:1) · No conversion applied`;
                            })()}
                            data-testid={`pm-uom-${r.rm_id}`}
                          >
                            {r.master_uom || "-"}
                            {r.is_dual_uom && r.conversion_factor ? (
                              <span className="ml-1 text-[10px] text-blue-600 font-mono">×{r.conversion_factor.toLocaleString(undefined, { maximumFractionDigits: 6 })}</span>
                            ) : null}
                          </TableCell>
                          <TableCell className="text-xs text-right font-mono">
                            {r.source === "MISSING" ? <span className="text-red-600">—</span> : <>Rs {r.avg_price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}{r.master_uom ? `/${r.master_uom.toLowerCase()}` : ""}</>}
                          </TableCell>
                          <TableCell className="text-xs">
                            {r.source === "invoice" && <span className="px-1.5 py-0.5 rounded text-[10px] bg-emerald-100 text-emerald-800">Invoice</span>}
                            {r.source === "stale_invoice" && (
                              <span
                                className="px-1.5 py-0.5 rounded text-[10px] bg-amber-100 text-amber-800"
                                title={r.stale_date ? `Most recent invoice: ${String(r.stale_date).slice(0, 10)} (outside ${priceMasterPagination.window_months || 3}-mo window)` : "Latest invoice older than rolling window"}
                              >
                                Stale{r.stale_date ? ` · ${String(r.stale_date).slice(0, 10)}` : ""}
                              </span>
                            )}
                            {r.source === "vendor_map" && <span className="px-1.5 py-0.5 rounded text-[10px] bg-blue-100 text-blue-800">Vendor map</span>}
                            {r.source === "rolled_up_bom" && <span className="px-1.5 py-0.5 rounded text-[10px] bg-purple-100 text-purple-800" title="Computed from this RM's L1 BOM">Rolled-up</span>}
                            {r.source === "MISSING" && <span className="px-1.5 py-0.5 rounded text-[10px] bg-red-100 text-red-700 font-semibold">Missing</span>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Pagination */}
              {!priceMasterLoading && priceMasterPagination.total > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-2 mt-3 text-xs text-muted-foreground" data-testid="pm-pagination">
                  <div>
                    Showing
                    <span className="font-mono mx-1">
                      {(priceMasterPagination.page - 1) * priceMasterPagination.page_size + 1}
                      {" – "}
                      {Math.min(priceMasterPagination.page * priceMasterPagination.page_size, priceMasterPagination.total)}
                    </span>
                    of <span className="font-mono">{priceMasterPagination.total}</span> RMs
                    <span className="ml-2 text-zinc-400">({priceMasterPagination.page_size}/page · prices computed on demand)</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      disabled={priceMasterPagination.page <= 1}
                      onClick={() => setPriceMasterPage(1)}
                      data-testid="pm-page-first"
                    >
                      « First
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      disabled={priceMasterPagination.page <= 1}
                      onClick={() => setPriceMasterPage(p => Math.max(1, p - 1))}
                      data-testid="pm-page-prev"
                    >
                      ‹ Prev
                    </Button>
                    <span className="px-2 font-mono">
                      Page {priceMasterPagination.page} / {priceMasterPagination.total_pages}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      disabled={priceMasterPagination.page >= priceMasterPagination.total_pages}
                      onClick={() => setPriceMasterPage(p => Math.min(priceMasterPagination.total_pages, p + 1))}
                      data-testid="pm-page-next"
                    >
                      Next ›
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      disabled={priceMasterPagination.page >= priceMasterPagination.total_pages}
                      onClick={() => setPriceMasterPage(priceMasterPagination.total_pages)}
                      data-testid="pm-page-last"
                    >
                      Last »
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      <Dialog open={showTagDialog} onOpenChange={setShowTagDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit RM - {editingRM?.rm_id}</DialogTitle>
            <DialogDescription>
              Update UOM, source type, and brand/vertical/model tags.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Brands */}
            <div>
              <Label>Brands</Label>
              <div className="flex flex-wrap gap-2 mt-2 p-3 border rounded-lg min-h-[60px]">
                {tagForm.brand_ids.map(bid => (
                  <Badge key={bid} variant="secondary" className="pr-1">
                    {getBrandCode(bid)} - {getBrandName(bid)}
                    <button 
                      className="ml-1 hover:text-red-500"
                      onClick={() => setTagForm({ 
                        ...tagForm, 
                        brand_ids: tagForm.brand_ids.filter(id => id !== bid) 
                      })}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <Select onValueChange={(v) => {
                if (v && !tagForm.brand_ids.includes(v)) {
                  setTagForm({ ...tagForm, brand_ids: [...tagForm.brand_ids, v] });
                }
              }}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Add brand..." />
                </SelectTrigger>
                <SelectContent>
                  {brands.filter(b => !tagForm.brand_ids.includes(b.id)).map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Verticals */}
            <div>
              <Label>Verticals</Label>
              <div className="flex flex-wrap gap-2 mt-2 p-3 border rounded-lg min-h-[60px]">
                {tagForm.vertical_ids.map(vid => (
                  <Badge key={vid} variant="outline" className="bg-blue-50 pr-1">
                    {getVerticalCode(vid)} - {getVerticalName(vid)}
                    <button 
                      className="ml-1 hover:text-red-500"
                      onClick={() => setTagForm({ 
                        ...tagForm, 
                        vertical_ids: tagForm.vertical_ids.filter(id => id !== vid) 
                      })}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <Select onValueChange={(v) => {
                if (v && !tagForm.vertical_ids.includes(v)) {
                  setTagForm({ ...tagForm, vertical_ids: [...tagForm.vertical_ids, v] });
                }
              }}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Add vertical..." />
                </SelectTrigger>
                <SelectContent>
                  {verticals.filter(v => !tagForm.vertical_ids.includes(v.id)).map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.code} - {v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Models */}
            <div>
              <Label>Models</Label>
              <div className="flex flex-wrap gap-2 mt-2 p-3 border rounded-lg min-h-[60px]">
                {tagForm.model_ids.map(mid => (
                  <Badge key={mid} variant="outline" className="bg-green-50 pr-1">
                    {getModelCode(mid)} - {getModelName(mid)}
                    <button 
                      className="ml-1 hover:text-red-500"
                      onClick={() => setTagForm({ 
                        ...tagForm, 
                        model_ids: tagForm.model_ids.filter(id => id !== mid) 
                      })}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <Select onValueChange={(v) => {
                if (v && !tagForm.model_ids.includes(v)) {
                  setTagForm({ ...tagForm, model_ids: [...tagForm.model_ids, v] });
                }
              }}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Add model..." />
                </SelectTrigger>
                <SelectContent>
                  {models.filter(m => !tagForm.model_ids.includes(m.id)).map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.code} - {m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Category — reassigning the category causes the
                Source Type below to automatically pick up the new
                category's `default_source_type`. */}
            <div>
              <Label>Category</Label>
              <Select
                value={tagForm.category || ""}
                onValueChange={(v) => setTagForm({ ...tagForm, category: v })}
              >
                <SelectTrigger data-testid="edit-category-select">
                  <SelectValue placeholder="Pick a category" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(rmCategories || {})
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([code, cfg]) => (
                      <SelectItem key={code} value={code}>
                        {code} — {cfg.name || code}
                        {cfg.default_source_type ? ` · ${cfg.default_source_type}` : ""}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {tagForm.category && tagForm.category !== editingRM?.category && (
                <p className="text-[11px] text-amber-700 mt-1">
                  Moving from <b>{editingRM?.category}</b> →{" "}
                  <b>{tagForm.category}</b>. Source Type will switch to{" "}
                  <b>
                    {rmCategories?.[tagForm.category]?.default_source_type ||
                      "—"}
                  </b>{" "}
                  on save.
                </p>
              )}
            </div>

            {/* UOM and Source Type */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>UOM</Label>
                <Select value={tagForm.uom} onValueChange={(v) => setTagForm({...tagForm, uom: v})}>
                  <SelectTrigger data-testid="edit-uom-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PCS">PCS (Pieces)</SelectItem>
                    <SelectItem value="KG">KG (Kilograms)</SelectItem>
                    <SelectItem value="GM">GM (Grams)</SelectItem>
                    <SelectItem value="MTR">MTR (Meters)</SelectItem>
                    <SelectItem value="LTR">LTR (Litres)</SelectItem>
                    <SelectItem value="SET">SET</SelectItem>
                    <SelectItem value="PAIR">PAIR</SelectItem>
                    <SelectItem value="ROLL">ROLL</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Source Type</Label>
                <div
                  className="h-10 px-3 rounded-md border border-input bg-muted text-sm flex items-center justify-between"
                  data-testid="edit-source-type-readonly"
                  title="Source type is inherited from the category and cannot be edited per-RM"
                >
                  {(() => {
                    // Reflect the IN-PROGRESS category choice so the user
                    // sees what Source Type will be after saving.
                    const cat = tagForm.category || editingRM?.category;
                    const def = cat ? rmCategories[cat]?.default_source_type : null;
                    if (!def) {
                      return (
                        <>
                          <span className="text-red-600 font-medium">Unconfigured</span>
                          <span className="text-[10px] text-red-600">Set on category</span>
                        </>
                      );
                    }
                    return (
                      <>
                        <span className="font-medium">{def}</span>
                        <span className="text-[10px] text-muted-foreground">from {cat}</span>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Dual UOM Toggle */}
            <div className="space-y-3 border-t pt-3">
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="edit-dual-uom" 
                  checked={tagForm.dual_uom} 
                  onChange={(e) => setTagForm({...tagForm, dual_uom: e.target.checked})}
                  className="h-4 w-4 rounded border-gray-300"
                  data-testid="edit-dual-uom-toggle"
                />
                <Label htmlFor="edit-dual-uom" className="text-sm cursor-pointer">
                  Different UOM for procurement & consumption
                </Label>
              </div>
              
              {tagForm.dual_uom && (
                <div className="grid grid-cols-3 gap-3 pl-6">
                  <div>
                    <Label className="text-xs">Procurement UOM</Label>
                    {/* Sep 2026 — no fake default: an untouched
                        dropdown renders the placeholder, never a
                        phantom value the state doesn't hold. */}
                    <Select value={tagForm.procurement_uom} onValueChange={(v) => setTagForm({...tagForm, procurement_uom: v})}>
                      <SelectTrigger className="h-8 text-xs" data-testid="edit-procurement-uom">
                        <SelectValue placeholder="Select…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="KG">KG</SelectItem>
                        <SelectItem value="GM">GM</SelectItem>
                        <SelectItem value="MTR">MTR</SelectItem>
                        <SelectItem value="LTR">LTR</SelectItem>
                        <SelectItem value="ROLL">ROLL</SelectItem>
                        <SelectItem value="CARTON">CARTON (Master Carton)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Consumption UOM</Label>
                    <Select value={tagForm.consumption_uom} onValueChange={(v) => setTagForm({...tagForm, consumption_uom: v})}>
                      <SelectTrigger className="h-8 text-xs" data-testid="edit-consumption-uom">
                        <SelectValue placeholder="Select…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PCS">PCS</SelectItem>
                        <SelectItem value="GM">GM</SelectItem>
                        <SelectItem value="SET">SET</SelectItem>
                        <SelectItem value="PAIR">PAIR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Conversion Factor</Label>
                    <Input 
                      type="number" 
                      step="0.0001"
                      value={tagForm.conversion_factor} 
                      onChange={(e) => setTagForm({...tagForm, conversion_factor: e.target.value})}
                      placeholder="e.g. 0.005"
                      className="h-8 text-xs font-mono"
                      data-testid="edit-conversion-factor"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">1 consumption unit = X procurement units</p>
                  </div>
                </div>
              )}
            </div>

            {/* Brand Specific Flag */}
            <div className="flex items-center gap-2">
              <Checkbox 
                checked={tagForm.is_brand_specific}
                onCheckedChange={(checked) => setTagForm({ ...tagForm, is_brand_specific: checked })}
              />
              <Label>Mark as Brand Specific RM</Label>
            </div>

            <Separator />

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowTagDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveTags} data-testid="save-tags-btn">
                Save Tags
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Tag Dialog */}
      <Dialog open={showBulkTagDialog} onOpenChange={setShowBulkTagDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk Add Tags</DialogTitle>
            <DialogDescription>
              Add tags to {selectedRMs.size} selected raw materials. Existing tags will be preserved.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Brands */}
            <div>
              <Label>Add Brands</Label>
              <div className="flex flex-wrap gap-2 mt-2 p-3 border rounded-lg min-h-[40px]">
                {tagForm.brand_ids.map(bid => (
                  <Badge key={bid} variant="secondary" className="pr-1">
                    {getBrandCode(bid)}
                    <button 
                      className="ml-1 hover:text-red-500"
                      onClick={() => setTagForm({ 
                        ...tagForm, 
                        brand_ids: tagForm.brand_ids.filter(id => id !== bid) 
                      })}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <Select onValueChange={(v) => {
                if (v && !tagForm.brand_ids.includes(v)) {
                  setTagForm({ ...tagForm, brand_ids: [...tagForm.brand_ids, v] });
                }
              }}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select brand..." />
                </SelectTrigger>
                <SelectContent>
                  {brands.filter(b => !tagForm.brand_ids.includes(b.id)).map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Verticals */}
            <div>
              <Label>Add Verticals</Label>
              <Select onValueChange={(v) => {
                if (v && !tagForm.vertical_ids.includes(v)) {
                  setTagForm({ ...tagForm, vertical_ids: [...tagForm.vertical_ids, v] });
                }
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select vertical..." />
                </SelectTrigger>
                <SelectContent>
                  {verticals.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.code} - {v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex flex-wrap gap-1 mt-2">
                {tagForm.vertical_ids.map(vid => (
                  <Badge key={vid} variant="outline" className="bg-blue-50">
                    {getVerticalCode(vid)}
                    <button className="ml-1" onClick={() => setTagForm({ ...tagForm, vertical_ids: tagForm.vertical_ids.filter(id => id !== vid) })}>
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Models */}
            <div>
              <Label>Add Models</Label>
              <Select onValueChange={(v) => {
                if (v && !tagForm.model_ids.includes(v)) {
                  setTagForm({ ...tagForm, model_ids: [...tagForm.model_ids, v] });
                }
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select model..." />
                </SelectTrigger>
                <SelectContent>
                  {models.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.code} - {m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex flex-wrap gap-1 mt-2">
                {tagForm.model_ids.map(mid => (
                  <Badge key={mid} variant="outline" className="bg-green-50">
                    {getModelCode(mid)}
                    <button className="ml-1" onClick={() => setTagForm({ ...tagForm, model_ids: tagForm.model_ids.filter(id => id !== mid) })}>
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Brand Specific */}
            <div className="flex items-center gap-2">
              <Checkbox 
                checked={tagForm.is_brand_specific}
                onCheckedChange={(checked) => setTagForm({ ...tagForm, is_brand_specific: checked })}
              />
              <Label>Mark as Brand Specific</Label>
            </div>

            <Separator />

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowBulkTagDialog(false)}>Cancel</Button>
              <Button onClick={handleBulkTag} data-testid="bulk-tag-btn">
                Add Tags to {selectedRMs.size} RMs
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add RM Dialog */}
      {/* Convert Category dialog (MasterAdmin only) */}
      <ConvertCategoryDialog
        rm={convertTargetRm}
        rmCategories={rmCategories}
        open={!!convertTargetRm}
        onClose={() => setConvertTargetRm(null)}
        onSuccess={() => {
          setConvertTargetRm(null);
          fetchMaterials();
        }}
      />

      <Dialog open={showAddDialog} onOpenChange={(open) => { setShowAddDialog(open); if (!open) resetAddForm(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Add New Raw Material</DialogTitle>
            <DialogDescription>Create a new RM with auto-generated code</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Category *</Label>
              <Select 
                value={selectedAddCategory || "select"} 
                onValueChange={(v) => { 
                  const cat = v === "select" ? "" : v;
                  setSelectedAddCategory(cat); 
                  setCategoryData({}); 
                  if (cat && rmCategories[cat]) {
                    setAddUom(rmCategories[cat].default_uom || "PCS");
                    setAddSourceType(rmCategories[cat].default_source_type || "PURCHASED");
                  } else {
                    setAddUom("");
                    setAddSourceType("");
                  }
                }}
              >
                <SelectTrigger data-testid="add-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(rmCategories).map(([code, cat]) => (
                    <SelectItem key={code} value={code}>{code} - {cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {selectedAddCategory && rmCategories[selectedAddCategory] && (
              <>
                {selectedAddCategory === "INP" ? (
                  <MouldDrivenInpFields
                    mouldSearchTerm={mouldSearchTerm}
                    setMouldSearchTerm={setMouldSearchTerm}
                    mouldOptions={mouldOptions}
                    mouldSearchLoading={mouldSearchLoading}
                    selectedMould={selectedMould}
                    setSelectedMould={setSelectedMould}
                    inpPantoneCode={inpPantoneCode}
                    setInpPantoneCode={setInpPantoneCode}
                    inpMbRmId={inpMbRmId}
                    setInpMbRmId={setInpMbRmId}
                    inpPreview={inpPreview}
                    inpPreviewLoading={inpPreviewLoading}
                  />
                ) : selectedAddCategory === "REGRIND" ? (
                  <RegrindRMFields
                    categoryData={categoryData}
                    setCategoryData={setCategoryData}
                  />
                ) : selectedAddCategory === "INMFAB" ? (
                  <div className="border-2 border-emerald-200 bg-emerald-50 rounded-lg p-3 space-y-3" data-testid="inmfab-fields">
                    <p className="text-xs text-emerald-900 font-medium">
                      INMFAB (Fabricated Base Part) — carries the powder-coat
                      dosage that every linked INM inherits.
                    </p>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Input
                        placeholder="e.g. Frame Handle 20mm"
                        value={inmfabDescription}
                        onChange={(e) => setInmfabDescription(e.target.value)}
                        data-testid="inmfab-description-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Powder Qty (grams) *</Label>
                        <Input
                          type="number" step="0.01" min="0"
                          value={inmfabPowderQtyGrams}
                          onChange={(e) => setInmfabPowderQtyGrams(e.target.value)}
                          placeholder="e.g. 60"
                          data-testid="inmfab-powder-qty-input"
                        />
                        <p className="text-[10px] text-emerald-800 mt-0.5">
                          Grams of powder per finished piece.
                        </p>
                      </div>
                      <div>
                        <Label>Coating Scrap Factor</Label>
                        <Input
                          type="number" step="0.01" min="0"
                          value={inmfabCoatingScrapFactor}
                          onChange={(e) => setInmfabCoatingScrapFactor(e.target.value)}
                          placeholder="0.10"
                          data-testid="inmfab-scrap-factor-input"
                        />
                        <p className="text-[10px] text-emerald-800 mt-0.5">
                          Fractional waste (0.10 = 10%).
                        </p>
                      </div>
                    </div>
                    <p className="text-[11px] text-emerald-800 pt-1 border-t border-emerald-200">
                      Editing these dosage fields later requires the explicit
                      cascade endpoint (INMFAB → INM). Silent PUT edits are
                      refused with HTTP 409.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="p-3 bg-gray-50 border rounded-lg">
                      <p className="text-sm font-medium mb-2">Required fields for {selectedAddCategory}:</p>
                      <div className="flex flex-wrap gap-2">
                        {rmCategories[selectedAddCategory].fields
                          // INM: pantone_code is shown in the dedicated
                          // amber block below — skip the duplicate chip.
                          .filter(field => !(selectedAddCategory === "INM" && field === "pantone_code"))
                          .map(field => (
                            <span key={field} className="text-xs bg-gray-200 px-2 py-1 rounded">{formatFieldName(field)}</span>
                          ))}
                      </div>
                    </div>

                    {rmCategories[selectedAddCategory].fields.map(field => {
                      // Aug 2026: INM now includes `pantone_code` as a
                      // schema field (so the bulk template emits the
                      // column and the upload backend can dedup on it).
                      // The Add-RM dialog already renders a dedicated
                      // amber "Pantone Code *" block below; skip the
                      // generic input here to avoid double-prompting
                      // for the same value.
                      if (selectedAddCategory === "INM" && field === "pantone_code") return null;
                      // Aug 2026 (BS Variant) — surface `required: true`
                      // from the DB-driven config so BS Variant Name
                      // renders with a red asterisk and the browser
                      // itself blocks submit when empty. Works for any
                      // future category that marks an identity field
                      // required in `rm_categories.description_columns`.
                      const descCol = (rmCategories[selectedAddCategory]?.description_columns || [])
                        .find(c => c.key === field);
                      const isRequired = !!descCol?.required;
                      return (
                        <div key={field}>
                          <Label>
                            {formatFieldName(field)}
                            {isRequired && <span className="text-red-600 ml-0.5">*</span>}
                          </Label>
                          <Input
                            placeholder={`Enter ${formatFieldName(field).toLowerCase()}`}
                            value={categoryData[field] || ''}
                            onChange={(e) => setCategoryData({...categoryData, [field]: e.target.value})}
                            required={isRequired}
                            data-testid={`add-rm-field-${field}`}
                          />
                        </div>
                      );
                    })}

                    {selectedAddCategory === "INM" && (
                      <div className="border-2 border-amber-200 bg-amber-50 rounded-lg p-3 space-y-3" data-testid="inm-pantone-block">
                        {/* Feb 2026 (Commit 2) — INMFAB parent + Source Type */}
                        <div className="space-y-2 pb-2 border-b border-amber-200">
                          <Label className="text-amber-900">Source Type</Label>
                          <div className="flex gap-4 text-sm">
                            {["MANUFACTURED", "PURCHASED", "BOTH"].map((st) => (
                              <label key={st} className="flex items-center gap-1 cursor-pointer">
                                <input
                                  type="radio"
                                  name="inm-source-type"
                                  value={st}
                                  checked={inmSourceType === st}
                                  onChange={() => setInmSourceType(st)}
                                  data-testid={`inm-source-type-${st.toLowerCase()}`}
                                />
                                <span>{st}</span>
                              </label>
                            ))}
                          </div>
                          <p className="text-[11px] text-amber-800">
                            MANUFACTURED INMs require an INMFAB parent. PURCHASED
                            skips the consuming BOM entirely.
                          </p>
                        </div>
                        <div className="space-y-2 pb-2 border-b border-amber-200">
                          <Label className="text-amber-900">
                            INMFAB Parent {inmSourceType === "MANUFACTURED" && <span className="text-red-600">*</span>}
                          </Label>
                          <Input
                            value={inmParentSearchTerm || inmParentRmId}
                            onChange={(e) => {
                              setInmParentSearchTerm(e.target.value);
                              setInmParentRmId("");
                            }}
                            placeholder="Search INMFAB rm_id or description (min 2 chars)"
                            data-testid="inm-parent-search-input"
                          />
                          {inmParentLoading && (
                            <p className="text-[11px] text-amber-800">Searching…</p>
                          )}
                          {inmParentOptions.length > 0 && (
                            <div className="border rounded-md bg-white max-h-40 overflow-auto" data-testid="inm-parent-options">
                              {inmParentOptions.map((opt) => (
                                <button
                                  key={opt.item_id}
                                  type="button"
                                  className={`w-full text-left px-2 py-1.5 text-xs hover:bg-emerald-50 border-b last:border-b-0 ${
                                    inmParentRmId === opt.item_id ? "bg-emerald-100 font-medium" : ""
                                  }`}
                                  onClick={() => {
                                    setInmParentRmId(opt.item_id);
                                    setInmParentSearchTerm(`${opt.item_id} — ${opt.name}`);
                                    setInmParentOptions([]);
                                  }}
                                  data-testid={`inm-parent-option-${opt.item_id}`}
                                >
                                  <span className="font-mono">{opt.item_id}</span> — {opt.name}
                                </button>
                              ))}
                            </div>
                          )}
                          {inmParentRmId && (
                            <p className="text-[11px] text-emerald-700">
                              Selected INMFAB: <span className="font-mono">{inmParentRmId}</span>
                            </p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label className="text-amber-900">Pantone Code <span className="text-red-600">*</span> (mandatory for INM)</Label>
                          <Input
                            value={categoryData.pantone_code || ""}
                            onChange={(e) => setCategoryData(prev => ({ ...prev, pantone_code: e.target.value }))}
                            placeholder="e.g. 427 C"
                            data-testid="inm-pantone-input"
                          />
                          <p className="text-[11px] text-amber-800">
                            Jul 2026: INM is now pantone-driven. Backend find-or-mints the
                            POWDER_COAT colourant on this pantone — no separate colourant
                            picker needed. Pantone must exist in the master (Tech Ops →
                            Pantones) first.
                          </p>
                        </div>
                        <div className="space-y-2 pt-2 border-t border-amber-200">
                          <Label className="text-amber-900">PWD RM ID <span className="text-[11px] text-amber-700">(optional)</span></Label>
                          <Input
                            value={inmPwdRmId}
                            onChange={(e) => setInmPwdRmId(e.target.value)}
                            placeholder="e.g. PWD_00045"
                            data-testid="inm-pwd-rm-id-input"
                          />
                          <p className="text-[11px] text-amber-800">
                            If supplied, promotes this PWD to PREFERRED for the resolved
                            colourant. Leave blank to have procurement attach one later via
                            Awaiting Sourcing.
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                )}
                
                <div>
                  <Label>Low Stock Threshold</Label>
                  <Input
                    type="number"
                    value={lowStockThreshold}
                    onChange={(e) => setLowStockThreshold(parseInt(e.target.value) || 10)}
                    min="1"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>UOM</Label>
                    <Select value={addUom || "PCS"} onValueChange={setAddUom}>
                      <SelectTrigger data-testid="add-uom-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PCS">PCS (Pieces)</SelectItem>
                        <SelectItem value="KG">KG (Kilograms)</SelectItem>
                        <SelectItem value="GM">GM (Grams)</SelectItem>
                        <SelectItem value="MTR">MTR (Meters)</SelectItem>
                        <SelectItem value="LTR">LTR (Litres)</SelectItem>
                        <SelectItem value="SET">SET</SelectItem>
                        <SelectItem value="PAIR">PAIR</SelectItem>
                        <SelectItem value="ROLL">ROLL</SelectItem>
                        <SelectItem value="CARTON">CARTON (Master Carton)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Source Type</Label>
                    <div
                      className="h-10 px-3 rounded-md border border-input bg-muted text-sm flex items-center justify-between"
                      data-testid="add-source-type-readonly"
                      title="Source type is inherited from the category and cannot be set per-RM"
                    >
                      {(() => {
                        const def = rmCategories[selectedAddCategory]?.default_source_type;
                        if (!def) {
                          return (
                            <>
                              <span className="text-red-600 font-medium">Unconfigured</span>
                              <span className="text-[10px] text-red-600">Set on category</span>
                            </>
                          );
                        }
                        return (
                          <>
                            <span className="font-medium">{def}</span>
                            <span className="text-[10px] text-muted-foreground">from {selectedAddCategory}</span>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
                
                {/* Dual UOM Toggle */}
                <div className="space-y-3 border-t pt-3">
                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      id="add-dual-uom" 
                      checked={addDualUom} 
                      onChange={(e) => setAddDualUom(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300"
                      data-testid="add-dual-uom-toggle"
                    />
                    <Label htmlFor="add-dual-uom" className="text-sm cursor-pointer">
                      Different UOM for procurement & consumption
                    </Label>
                  </div>
                  
                  {addDualUom && (
                    <div className="grid grid-cols-3 gap-3 pl-6">
                      <div>
                        <Label className="text-xs">Procurement UOM</Label>
                        {/* Sep 2026 — no fake default: an untouched
                            dropdown renders the placeholder, never a
                            phantom value the state doesn't hold. */}
                        <Select value={addProcurementUom} onValueChange={setAddProcurementUom}>
                          <SelectTrigger className="h-8 text-xs" data-testid="add-procurement-uom">
                            <SelectValue placeholder="Select…" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="KG">KG</SelectItem>
                            <SelectItem value="GM">GM</SelectItem>
                            <SelectItem value="MTR">MTR</SelectItem>
                            <SelectItem value="LTR">LTR</SelectItem>
                            <SelectItem value="ROLL">ROLL</SelectItem>
                            <SelectItem value="CARTON">CARTON (Master Carton)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Consumption UOM</Label>
                        <Select value={addConsumptionUom} onValueChange={setAddConsumptionUom}>
                          <SelectTrigger className="h-8 text-xs" data-testid="add-consumption-uom">
                            <SelectValue placeholder="Select…" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="PCS">PCS</SelectItem>
                            <SelectItem value="GM">GM</SelectItem>
                            <SelectItem value="SET">SET</SelectItem>
                            <SelectItem value="PAIR">PAIR</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Conversion Factor</Label>
                        <Input 
                          type="number" 
                          step="0.0001"
                          value={addConversionFactor} 
                          onChange={(e) => setAddConversionFactor(e.target.value)}
                          placeholder="e.g. 0.005"
                          className="h-8 text-xs font-mono"
                          data-testid="add-conversion-factor"
                        />
                        <p className="text-[10px] text-muted-foreground mt-0.5">1 consumption unit = X procurement units</p>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex justify-between pt-4">
                  <Button variant="outline" onClick={() => downloadCategoryTemplate(selectedAddCategory)}>
                    <Download className="w-4 h-4 mr-2" />
                    Download Template
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => { setShowAddDialog(false); resetAddForm(); }}>
                      Cancel
                    </Button>
                    {selectedAddCategory === "INP" ? (
                      <Button
                        onClick={handleAddInpFromMould}
                        disabled={!selectedMould || !inpPantoneCode || inpPreviewLoading}
                        data-testid="save-inp-from-mould-btn"
                      >
                        {inpPreview?.duplicate_of ? "Re-activate / Update" : "Create INP from Mould"}
                      </Button>
                    ) : (
                      <Button onClick={handleAddRM} data-testid="save-rm-btn">
                        Create RM
                      </Button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Created RMs Dialog - Shows after successful bulk upload */}
      <Dialog open={showCreatedDialog} onOpenChange={setShowCreatedDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Created Raw Materials</DialogTitle>
            <DialogDescription>{createdRMs.length} new RMs created successfully</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>RM ID</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>BOM</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {createdRMs.map((rm, idx) => {
                    // Jul 2026 — join the per-row INM BOM outcome onto
                    // the created-RM row. Non-INMs (and any INM row
                    // whose outcome the backend didn't emit) show a
                    // muted "—".
                    const _bom = (inmBomOutcomes || []).find(o => o.rm_id === rm.rm_id);
                    return (
                      <TableRow key={idx}>
                        <TableCell className="font-mono">{rm.rm_id}</TableCell>
                        <TableCell><Badge variant="outline">{rm.category}</Badge></TableCell>
                        <TableCell className="text-gray-500 text-sm">
                          {Object.entries(rm.category_data || {}).slice(0, 3).map(([k, v]) => `${k}: ${v}`).join(', ')}
                        </TableCell>
                        <TableCell className="text-xs">
                          {_bom ? (
                            _bom.bom_written ? (
                              <Badge className="bg-green-100 text-green-800 border-green-300">✓ written</Badge>
                            ) : (
                              <span className="text-amber-700 font-mono" title={_bom.reason || ""}>
                                {_bom.reason || "no BOM"}
                              </span>
                            )
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setShowCreatedDialog(false)}>Close</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Migration Dialog */}
      <Dialog open={showMigrateDialog} onOpenChange={setShowMigrateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Migrate RM Data</DialogTitle>
            <DialogDescription>Import RM data from a JSON export file</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              This is used for migrating data between environments.
            </p>
            <input
              type="file"
              ref={migrateFileInputRef}
              onChange={handleMigrateImport}
              accept=".json"
              className="hidden"
            />
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => migrateFileInputRef.current?.click()}
                disabled={migrating}
                className="flex-1"
              >
                <Database className="w-4 h-4 mr-2" />
                {migrating ? "Importing..." : "Select JSON File"}
              </Button>
              <Button variant="outline" onClick={() => setShowMigrateDialog(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Vendors-for-RM dialog (Feb 2026) */}
      <Dialog open={showVendorsDialog} onOpenChange={setShowVendorsDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Vendors tagged to{" "}
              <span className="font-mono">{vendorsForRMTarget?.rm_id}</span>
            </DialogTitle>
            <DialogDescription>
              {vendorsForRMTarget?.description || vendorsForRMTarget?.category_data?.name || vendorsForRMTarget?.category}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto" data-testid="vendors-for-rm-list">
            {vendorsForRMLoading ? (
              <div className="text-center py-10 text-zinc-500 text-sm">Loading…</div>
            ) : vendorsForRM.length === 0 ? (
              <div className="text-center py-10 text-sm text-zinc-500">
                No vendors tagged to this RM yet.
                <div className="text-xs text-zinc-400 mt-1">
                  Tag vendors under <strong>Procurement → Vendor Management</strong>.
                </div>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vendor</TableHead>
                    <TableHead className="text-right">Unit Price</TableHead>
                    <TableHead>UOM</TableHead>
                    <TableHead>Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...vendorsForRM]
                    .sort((a, b) => Number(a.price || 0) - Number(b.price || 0))
                    .map((v, i) => (
                      <TableRow key={`${v.id || v.vendor_id || i}`} data-testid={`vendor-row-${v.vendor_id || i}`}>
                        <TableCell>
                          <div className="font-medium text-zinc-900">{v.vendor_name || v.vendor_id || "Unknown"}</div>
                          {v.vendor_id && (
                            <div className="text-[10px] font-mono text-zinc-400">{v.vendor_id}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {v.price != null ? `${v.currency || "₹"} ${Number(v.price).toFixed(2)}` : "—"}
                        </TableCell>
                        <TableCell className="text-xs text-zinc-600">
                          {vendorsForRMTarget?.uom || vendorsForRMTarget?.procurement_uom || "—"}
                        </TableCell>
                        <TableCell className="text-xs text-zinc-500">
                          {v.updated_at ? new Date(v.updated_at).toLocaleDateString() : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVendorsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Jul 2026 — INMFAB Dosage Cascade Dialog (preview → apply → undo) */}
      <Dialog open={!!dosageDialogRm} onOpenChange={(v) => { if (!v) closeDosageDialog(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="inmfab-dosage-dialog">
          <DialogHeader>
            <DialogTitle>
              Edit INMFAB dosage · <span className="font-mono">{dosageDialogRm?.rm_id}</span>
            </DialogTitle>
            <DialogDescription>
              INMFAB dosage changes are refused by a direct PUT (409 by design). This dialog runs the guarded cascade — <b>preview → apply → undo</b> — so every linked MANUFACTURED INM&apos;s BOM is re-derived atomically.
            </DialogDescription>
          </DialogHeader>

          {/* Step 1 — inputs */}
          {dosageStep === "input" && dosageDialogRm && (
            <div className="space-y-4">
              <div className="border rounded-md p-3 bg-emerald-50 border-emerald-200">
                <div className="text-xs font-semibold mb-1 text-emerald-900">Current dosage (read-only)</div>
                <div className="flex gap-4 text-sm">
                  <div><span className="text-muted-foreground">Powder qty:</span> <span className="font-mono">{dosageDialogRm.category_data?.powder_qty_grams ?? "?"} g</span></div>
                  <div><span className="text-muted-foreground">Scrap factor:</span> <span className="font-mono">{dosageDialogRm.category_data?.coating_scrap_factor ?? "?"}</span></div>
                  <div><span className="text-muted-foreground">Linked INMs:</span> <span className="font-mono">{linkedInmCounts[(dosageDialogRm.rm_id || "").toUpperCase()] || 0}</span></div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>New Powder Qty (grams) *</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={dosageNewPg}
                    onChange={(e) => setDosageNewPg(e.target.value)}
                    placeholder="e.g. 30"
                    className="font-mono"
                    data-testid="dosage-new-pg"
                  />
                </div>
                <div>
                  <Label>New Coating Scrap Factor</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={dosageNewScrap}
                    onChange={(e) => setDosageNewScrap(e.target.value)}
                    placeholder="e.g. 0.10 (10% overspray)"
                    className="font-mono"
                    data-testid="dosage-new-scrap"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Reason (optional)</Label>
                  <Input
                    value={dosageReason}
                    onChange={(e) => setDosageReason(e.target.value)}
                    placeholder="e.g. Vendor spec revised, dosage calibration"
                    data-testid="dosage-reason"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2 — preview */}
          {dosageStep === "preview" && dosagePreview && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <StatCard label="Linked INMs (manufactured)"
                          value={dosagePreview.linked_inm_manufactured || 0}
                          testid="dosage-preview-manufactured" />
                <StatCard label="Skipped (purchased)"
                          value={dosagePreview.linked_inm_purchased_skipped || 0} />
                <StatCard label="Skipped (no colourant)"
                          value={dosagePreview.linked_inm_no_colourant_skipped || 0} />
                <StatCard label="Open production schedules"
                          value={dosagePreview.open_production_schedules || 0}
                          warn={(dosagePreview.open_production_schedules || 0) > 0}
                          testid="dosage-preview-open-prod" />
                <StatCard label="Open dispatch lots"
                          value={dosagePreview.open_dispatch_lots || 0}
                          warn={(dosagePreview.open_dispatch_lots || 0) > 0}
                          testid="dosage-preview-open-dispatch" />
              </div>

              {(dosagePreview.dosage_delta && !dosagePreview.dosage_delta.changed) && (
                <div className="rounded-md p-3 border bg-yellow-50 border-yellow-200 text-xs text-yellow-900">
                  ⚠ Proposed dosage equals current dosage. Applying will still snapshot but rewrite is a no-op.
                </div>
              )}

              {((dosagePreview.open_production_schedules || 0) > 0
                || (dosagePreview.open_dispatch_lots || 0) > 0) && (
                <div className="rounded-md p-3 border bg-red-50 border-red-200 text-xs text-red-900" data-testid="dosage-preview-blocked">
                  <b>Cannot apply — resolve open production first.</b> The backend snapshots &amp; cascades even now, but any consumption already in-flight will drift. Please close the open production schedules and dispatch lots, then re-preview.
                </div>
              )}

              {(dosagePreview.sample_manufactured_inms || []).length > 0 && (
                <div className="border rounded-md">
                  <div className="text-xs font-semibold p-2 bg-slate-50 border-b">Sample linked INMs (first 10)</div>
                  <div className="max-h-40 overflow-y-auto text-xs">
                    {dosagePreview.sample_manufactured_inms.map(x => (
                      <div key={x.rm_id} className="flex justify-between px-2 py-1 border-b last:border-b-0">
                        <span className="font-mono">{x.rm_id}</span>
                        <span className="text-muted-foreground truncate ml-2">{x.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3 — success */}
          {dosageStep === "success" && dosageResult && (
            <div className="space-y-3" data-testid="dosage-success">
              <div className="rounded-md p-3 border bg-green-50 border-green-200 text-sm text-green-900">
                <b>Cascade complete.</b> New dosage stored and {dosageResult.rewritten || 0} linked INM BOM(s) re-derived.
                {(dosageResult.skipped || 0) > 0 && ` ${dosageResult.skipped} skipped.`}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <StatCard label="Rewritten" value={dosageResult.rewritten || 0} testid="dosage-result-rewritten" />
                <StatCard label="Skipped" value={dosageResult.skipped || 0} />
              </div>
              <div className="border rounded-md p-2 text-xs bg-slate-50 flex items-center gap-2">
                <span className="text-muted-foreground">cascade_id:</span>
                <span className="font-mono select-all" data-testid="dosage-cascade-id">{dosageResult.cascade_id}</span>
                <Button
                  variant="ghost" size="sm" className="ml-auto"
                  onClick={() => { navigator.clipboard.writeText(dosageResult.cascade_id); toast.success("Copied cascade_id"); }}
                >Copy</Button>
              </div>
            </div>
          )}

          <DialogFooter>
            {dosageStep === "input" && (
              <>
                <Button variant="outline" onClick={closeDosageDialog}>Cancel</Button>
                <Button
                  onClick={runDosagePreview}
                  disabled={dosageBusy || !dosageNewPg}
                  data-testid="dosage-preview-btn"
                >{dosageBusy ? "Previewing…" : "Preview impact"}</Button>
              </>
            )}
            {dosageStep === "preview" && (
              <>
                <Button variant="outline" onClick={() => setDosageStep("input")} disabled={dosageBusy}>Back</Button>
                <Button
                  onClick={applyDosageCascade}
                  disabled={
                    dosageBusy
                    || (dosagePreview?.open_production_schedules || 0) > 0
                    || (dosagePreview?.open_dispatch_lots || 0) > 0
                  }
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="dosage-apply-btn"
                >{dosageBusy ? "Applying…" : "Apply cascade"}</Button>
              </>
            )}
            {dosageStep === "success" && (
              <>
                <Button
                  variant="outline"
                  onClick={undoDosageCascade}
                  disabled={dosageBusy}
                  data-testid="dosage-undo-btn"
                ><Undo2 className="w-4 h-4 mr-1" /> Undo cascade</Button>
                <Button onClick={closeDosageDialog}>Close</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RMRepository;

/* =========================================================================
 * Phase B.6 — Mould-driven INP fields (Feb 2026)
 *
 * Pure-presentational component used inside the Add RM dialog when the user
 * picks category=INP. Drives the canonical Phase-C INP creation flow:
 *   Mould Code typeahead → auto-fill polymer/weight/MB ratio → Colourant
 *   SKU picker → live BOM preview. NO legacy `mb` / `colour` fields.
 *
 * State lives in the parent (RMRepository). This component is just the
 * render layer.
 * ========================================================================= */
function MouldDrivenInpFields(props) {
  const {
    setMouldSearchTerm,
    selectedMould, setSelectedMould,
    inpPantoneCode, setInpPantoneCode,
    inpMbRmId, setInpMbRmId,
    inpPreview, inpPreviewLoading,
  } = props;

  return (
    <div className="space-y-4" data-testid="mould-driven-inp-fields">
      {/* Feb 2026 — shared MouldSearchSelect (also used by Convert →
          INP). Behaviour unchanged: type → click a row → summary card;
          picked mould's id flows into the parent's `selectedMould` and
          the create call sends `mould_id = selectedMould.id`. */}
      <MouldSearchSelect
        testIdPrefix="mould-search"
        label="Mould Code"
        required
        value={selectedMould}
        onSelect={(m) => { setSelectedMould(m); setMouldSearchTerm(""); }}
        onClear={() => { setSelectedMould(null); setMouldSearchTerm(""); }}
      />

      {/* Feb 2026 — Pantone Code + MB Code drive find-or-mint of the
          Colourant SKU on the backend. Operators no longer need to pre-
          create colourants. The MB is optional: if supplied, it gets
          attached as the PREFERRED MB on the (re)used colourant; if
          omitted, the INP enters the Awaiting Sourcing queue. */}
      <div className="space-y-2">
        <Label>Pantone Code <span className="text-red-600">*</span></Label>
        <Input
          value={inpPantoneCode || ""}
          onChange={(e) => setInpPantoneCode(e.target.value)}
          placeholder="e.g. 427 C"
          data-testid="inp-pantone-input"
        />
        <p className="text-[11px] text-stone-500">
          Pantone must already exist in the master library (Tech Ops → Pantones).
          The system will find an existing Colourant SKU on (polymer, pantone) or
          mint a new one automatically.
        </p>
      </div>

      <div className="space-y-2">
        <Label>MB Code <span className="text-stone-400">(optional)</span></Label>
        <Input
          value={inpMbRmId || ""}
          onChange={(e) => setInpMbRmId(e.target.value)}
          placeholder="e.g. MB_00075 (leave blank to send to Awaiting Sourcing)"
          data-testid="inp-mb-input"
        />
        <p className="text-[11px] text-stone-500">
          If provided, the MB is linked to the colourant as PREFERRED so the
          derived BOM picks it up. Omit to mark the INP as PENDING_SOURCING.
        </p>
      </div>

      {/* Live BOM preview */}
      {selectedMould && false && (
        <div className="border rounded-md p-3 bg-slate-50" data-testid="inp-bom-preview">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm font-semibold">Live BOM Preview</Label>
            {inpPreviewLoading && <span className="text-[11px] text-stone-500">Loading…</span>}
          </div>

          {inpPreview?.duplicate_of && (
            <div className="text-xs bg-amber-100 border border-amber-300 rounded p-2 mb-2" data-testid="inp-duplicate-banner">
              <b>Heads-up:</b> An identical INP already exists →{" "}
              <span className="font-mono">{inpPreview.duplicate_of.rm_id}</span>
              {inpPreview.duplicate_of.description ? ` — ${inpPreview.duplicate_of.description}` : ""}.
              {" "}{inpPreview.duplicate_of.is_active === false
                ? "It is INACTIVE — submit will re-activate it."
                : "Submit will be blocked (409)."}
            </div>
          )}

          {inpPreview && !inpPreviewLoading && (
            <>
              <div className="text-[11px] text-stone-600 mb-2">
                Computed description:
                <span className="font-mono ml-1">{
                  [
                    inpPreview.category_data?.mould_code,
                    inpPreview.category_data?.model_name,
                    inpPreview.category_data?.part_name,
                  ].filter(Boolean).join(" / ")
                }</span>
              </div>
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-100 text-left">
                    <th className="p-2 border">RM</th>
                    <th className="p-2 border text-right">Qty</th>
                    <th className="p-2 border">UoM</th>
                    <th className="p-2 border">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {(inpPreview.bom_components || []).length === 0 && (
                    <tr><td colSpan={4} className="p-2 text-center text-stone-500">No BOM rows derived.</td></tr>
                  )}
                  {(inpPreview.bom_components || []).map((c, i) => (
                    <tr key={i} className="border-t" data-testid={`bom-row-${c.component_rm_id}`}>
                      <td className="p-2 border font-mono">{c.component_rm_id}</td>
                      <td className="p-2 border text-right font-mono">{c.quantity}</td>
                      <td className="p-2 border">{c.uom}</td>
                      <td className="p-2 border text-[10px] text-stone-500">{c.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {(inpPreview.warnings || []).length > 0 && (
                <div className="mt-2 text-[11px] bg-amber-50 border border-amber-200 rounded p-2">
                  <b>Warnings:</b>
                  <ul className="list-disc ml-4">
                    {inpPreview.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}

    </div>
  );
}

function StatCard({ label, value, warn, testid }) {
  return (
    <div
      className={`border rounded-md p-2 ${warn ? "bg-red-50 border-red-200" : "bg-slate-50"}`}
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold font-mono ${warn ? "text-red-800" : ""}`}>{value}</div>
    </div>
  );
}
